"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScriptEvaluator = void 0;
const Player_1 = require("../models/Player");
const InventoryManager_1 = require("../models/InventoryManager");
const EffectTracker_1 = require("./EffectTracker");
const VariableStore_1 = require("./VariableStore");
const GuestAdventureService_1 = require("./GuestAdventureService");
const SharedVariablesService_1 = require("./SharedVariablesService");
const ScriptContext_1 = require("./ScriptContext");
const entityScriptHelpers_1 = require("../entities/entityScriptHelpers");
const foreignEntityScriptHelpers_1 = require("../entities/foreignEntityScriptHelpers");
const scheduleEventHelpers_1 = require("./scheduleEventHelpers");
const ids_1 = require("../types/ids");
const LocationGraph_1 = require("./LocationGraph");
const TimerState_1 = require("./TimerState");
const timerDeadlineHelpers_1 = require("./timerDeadlineHelpers");
const dateTime_1 = require("../utils/dateTime");
const openingSessionStack_1 = require("./openingSessionStack");
const constants_1 = require("../activities/constants");
const characterActivityState_1 = require("../activities/characterActivityState");
const incompleteActivityChecks_1 = require("../activities/incompleteActivityChecks");
const adventureScriptValidation_1 = require("./adventureScriptValidation");
const variableDeclarationLookup_1 = require("./variableDeclarationLookup");
const characterInstanceNames_1 = require("../utils/characterInstanceNames");
const openCharacterRuntime_1 = require("../utils/openCharacterRuntime");
const characterMap_1 = require("./characterMap");
const sharedDataRichText_1 = require("../utils/sharedDataRichText");
/**
 * ScriptEvaluator - Executes JavaScript-based condition and effect scripts
 * in a controlled, sandboxed environment.
 */
class ScriptEvaluator {
    /** Override wall-clock for timer/schedule script builtins (tests and deterministic hosts). */
    static setNowMsProvider(provider) {
        ScriptEvaluator.nowMsProvider = provider;
    }
    static resolveNowMs() {
        return ScriptEvaluator.nowMsProvider?.() ?? Date.now();
    }
    /**
     * Execute a condition script and return boolean result
     */
    static executeCondition(script, player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkServiceOrEmail, emailService, emailActionTokenService, verificationCodeService, analyticsService, entitiesService, foreignEntityLookup) {
        // Empty or whitespace-only scripts are falsy
        if (!script || script.trim().length === 0) {
            return false;
        }
        // Create frozen context with read-only functions
        const inviteLinkService = this.isEmailService(inviteLinkServiceOrEmail) ? undefined : inviteLinkServiceOrEmail;
        const resolvedEmailService = emailService ?? (this.isEmailService(inviteLinkServiceOrEmail) ? inviteLinkServiceOrEmail : undefined);
        const context = this.createConditionContext(player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, resolvedEmailService, emailActionTokenService, analyticsService, entitiesService, foreignEntityLookup);
        const prelude = (0, adventureScriptValidation_1.getAdventurePrelude)(adventureManager, gameSession);
        const kind = (0, adventureScriptValidation_1.resolveConditionExecutionKind)(script);
        (0, adventureScriptValidation_1.validateExecutionScript)(prelude, script, kind, gameSession.adventureId);
        const result = this.execute(script, context, { executionKind: kind, prelude });
        return Boolean(result);
    }
    /**
     * Execute an effect script with mutation capabilities
     */
    static executeEffects(script, player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkServiceOrEmail, emailService, emailActionTokenService, verificationCodeService, analyticsService, sharedVariablesService, guestAdventureService, options) {
        // Track changes for return value
        const tracker = new EffectTracker_1.EffectTracker();
        // Empty or whitespace-only scripts do nothing
        if (!script || script.trim().length === 0) {
            return tracker.getResult();
        }
        try {
            // Create context with mutation functions
            const inviteLinkService = this.isEmailService(inviteLinkServiceOrEmail) ? undefined : inviteLinkServiceOrEmail;
            const resolvedEmailService = emailService ?? (this.isEmailService(inviteLinkServiceOrEmail) ? inviteLinkServiceOrEmail : undefined);
            const activityInitialisingIds = options?.activityInitialisingIds ?? new Set();
            const context = this.createEffectContext(player, gameSession, tracker, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, resolvedEmailService, emailActionTokenService, verificationCodeService, analyticsService, {
                activityInitialisingIds,
                entitiesService: options?.entitiesService,
                foreignEntityLookup: options?.foreignEntityLookup
            }, sharedVariablesService, guestAdventureService);
            const prelude = (0, adventureScriptValidation_1.getAdventurePrelude)(adventureManager, gameSession);
            (0, adventureScriptValidation_1.validateExecutionScript)(prelude, script, 'effect', gameSession.adventureId);
            this.execute(script, context, { executionKind: 'effect', prelude });
        }
        catch (error) {
            console.error(`❌ Effect script failed: "${script}"`, error);
            if (error instanceof Error) {
                console.error(`❌ Error message:`, error.message);
                console.error(`❌ Error stack:`, error.stack);
            }
            // Re-throw errors related to variable setting, as these are validation errors
            // that the game logic needs to handle
            if (error instanceof Error &&
                (error.message.includes('not declared in any scope') ||
                    error.message.includes('Shared variable') ||
                    error.message.includes('setSharedVariable requires') ||
                    error.message.includes('setSharedVariableForUser requires') ||
                    error.message.includes('createGuestAdventure requires') ||
                    error.message.includes('createGuestAdventure cannot') ||
                    error.message.includes('deleteGuestAdventureInvite requires'))) {
                console.error(`❌ This is a variable declaration error - re-throwing`);
                throw error;
            }
            // Return whatever changes were tracked before the error for other types of errors
        }
        return tracker.getResult();
    }
    /**
     * Run an NPC `update` script with character-scoped variable context for `npcId`.
     * Adds `getNpcLocation`, `setNpcLocation`, and `getNowMs` (override via options).
     * Errors are logged and swallowed so one bad NPC does not abort the tick.
     */
    static executeNpcUpdate(script, npcId, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, options) {
        const tracker = new EffectTracker_1.EffectTracker();
        if (!script || script.trim().length === 0) {
            return tracker.getResult();
        }
        const anchor = gameSession.players[0];
        const npcPlayer = new Player_1.Player({
            id: anchor?.id ?? ids_1.IdHelpers.toPlayerId('__npc_anchor__'),
            playerName: anchor?.playerName ?? '__npc_anchor__',
            characterName: npcId,
            currentCharacter: npcId,
            currentScene: anchor?.currentScene ?? '',
            userId: anchor?.userId,
            inventory: [],
            stats: [],
            sceneHistory: []
        });
        try {
            const base = this.createEffectContext(npcPlayer, gameSession, tracker, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService);
            const getNowMs = options?.getNowMs ?? (() => Date.now());
            const graph = (0, LocationGraph_1.getLocationGraphForAdventure)(gameSession.adventureId);
            const context = {
                ...base,
                getNowMs,
                getNpcLocation: () => {
                    const v = variableStore.getVariable('character_location', VariableStore_1.VariableStore.buildVariableContext(npcPlayer.id, npcId, npcPlayer.currentScene, npcPlayer.userId || 'anonymous'));
                    if (v === undefined || v === null || String(v).trim() === '') {
                        return null;
                    }
                    return String(v);
                },
                setNpcLocation: (locationId) => {
                    const val = locationId == null || locationId === '' ? '' : String(locationId);
                    base.setVariable('character_location', val);
                },
                getAdjacentLocations: (locationId) => {
                    if (!graph)
                        return [];
                    return graph.getAdjacentLocations(locationId);
                },
                getRandomElement: (items) => (0, LocationGraph_1.getRandomElement)(items)
            };
            const prelude = (0, adventureScriptValidation_1.getAdventurePrelude)(adventureManager, gameSession);
            (0, adventureScriptValidation_1.validateExecutionScript)(prelude, script, 'effect', gameSession.adventureId);
            this.execute(script, context, { executionKind: 'effect', prelude });
        }
        catch (error) {
            console.error(`[ScriptEvaluator] NPC update failed for '${npcId}':`, error);
            if (error instanceof Error) {
                console.error(`[ScriptEvaluator] NPC update message:`, error.message);
            }
            try {
                options?.onUpdateFailure?.();
            }
            catch {
                /* ignore observer errors */
            }
        }
        return tracker.getResult();
    }
    /**
     * Execute a script and return its value (for target evaluation)
     */
    static executeScriptWithReturnValue(script, player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkServiceOrEmail, emailService, emailActionTokenService, verificationCodeService, analyticsService, entitiesService, foreignEntityLookup, injectedVariables) {
        // Empty or whitespace-only scripts return empty string
        if (!script || script.trim().length === 0) {
            return '';
        }
        // Create frozen context with read-only functions (same as conditions)
        const inviteLinkService = this.isEmailService(inviteLinkServiceOrEmail) ? undefined : inviteLinkServiceOrEmail;
        const resolvedEmailService = emailService ?? (this.isEmailService(inviteLinkServiceOrEmail) ? inviteLinkServiceOrEmail : undefined);
        const context = {
            ...this.createConditionContext(player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, resolvedEmailService, emailActionTokenService, analyticsService, entitiesService, foreignEntityLookup),
            ...(0, sharedDataRichText_1.bindInjectedSharedScriptVariables)(injectedVariables)
        };
        const prelude = (0, adventureScriptValidation_1.getAdventurePrelude)(adventureManager, gameSession);
        const executionKind = (0, adventureScriptValidation_1.resolveExpressionExecutionKind)(script);
        (0, adventureScriptValidation_1.validateExecutionScript)(prelude, script, executionKind, gameSession.adventureId);
        const result = this.execute(script, context, { executionKind, prelude });
        // Convert result to string if it's not already a string
        // Handle null/undefined by returning empty string
        if (result === null || result === undefined) {
            return '';
        }
        return result;
    }
    /**
     * Create read-only context for condition evaluation
     */
    static createConditionContext(player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, emailService, emailActionTokenService, analyticsService, entitiesService, foreignEntityLookup) {
        const runConditionForPlayer = (script, targetPlayer) => ScriptEvaluator.executeCondition(script, targetPlayer, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, emailService, emailActionTokenService, undefined, analyticsService, entitiesService, foreignEntityLookup);
        const baseContext = (0, ScriptContext_1.createReadOnlyScriptContext)({
            player,
            gameSession,
            variableStore,
            adventureManager,
            friendsService,
            adventureInviteService,
            asyncOperationService,
            inviteLinkService,
            analyticsService,
            evaluateConditionScript: (script) => runConditionForPlayer(script, player),
            executeConditionForPlayer: runConditionForPlayer,
            getNowMs: () => ScriptEvaluator.resolveNowMs(),
            entitiesService,
            foreignEntityLookup,
        });
        return {
            ...baseContext,
            // ScriptEvaluator-specific read-only functions
            hasActiveAdventures: () => Boolean(player.hasActiveAdventures),
            peekStack: (name) => {
                if (!variableStore) {
                    console.warn(`peekStack called for '${name}' but no VariableStore available`);
                    return undefined;
                }
                const context = {
                    playerId: player.id,
                    characterName: player.characterName,
                    currentSceneId: player.currentScene,
                    userId: player.userId || 'anonymous'
                };
                return variableStore.peekStack(name, context);
            },
            isOpeningSessionStackTopValid: () => Boolean(player.openingSessionTopValid),
            /**
             * True when `frame` is exactly the current top of `opening_session_id` and the server marked that top as valid.
             * For arbitrary historical frames (not equal to peek), returns false.
             */
            isOpeningSessionValid: (frame) => {
                if (!frame || typeof frame !== 'string')
                    return false;
                if (!(0, openingSessionStack_1.parseOpeningSessionFrame)(frame))
                    return false;
                if (!variableStore)
                    return false;
                const context = {
                    playerId: player.id,
                    characterName: player.characterName,
                    currentSceneId: player.currentScene,
                    userId: player.userId || 'anonymous'
                };
                const top = variableStore.peekStack(openingSessionStack_1.OPENING_SESSION_STACK_VAR, context);
                if (!top || top !== frame)
                    return false;
                return Boolean(player.openingSessionTopValid);
            },
            hasFriendRequestFrom: (userId) => {
                if (!friendsService || !player.userId)
                    return false;
                return friendsService.hasFriendRequestFrom(player.userId, userId);
            },
            hasSentFriendRequestTo: (userId) => {
                if (!friendsService || !player.userId)
                    return false;
                return friendsService.hasSentFriendRequestTo(player.userId, userId);
            },
            isFriend: (userId) => {
                if (!friendsService || !player.userId)
                    return false;
                return friendsService.isFriend(player.userId, userId);
            },
            getFriendCount: () => {
                if (!friendsService || !player.userId)
                    return 0;
                return friendsService.getFriendCount(player.userId);
            },
            getPendingReceivedRequestCount: () => {
                if (!friendsService || !player.userId)
                    return 0;
                return friendsService.getPendingReceivedRequestCount(player.userId);
            },
            getPendingSentRequestCount: () => {
                if (!friendsService || !player.userId)
                    return 0;
                return friendsService.getPendingSentRequestCount(player.userId);
            },
            hasAdventureInvite: (sessionId) => {
                if (!adventureInviteService || !player.userId)
                    return false;
                const invites = adventureInviteService.getInvites(player.userId);
                if (sessionId) {
                    return invites.some(inv => inv.sessionId === sessionId && inv.status === 'pending');
                }
                return invites.length > 0;
            },
            hasPendingAdventureInvite: (sessionId) => {
                if (!adventureInviteService || !player.userId)
                    return false;
                const invites = adventureInviteService.getInvites(player.userId);
                return invites.some(inv => inv.sessionId === sessionId && inv.status === 'pending');
            },
            getPendingAdventureInviteId: (sessionId, playerSlot) => {
                if (!adventureInviteService)
                    return undefined;
                const userIdToUse = player.userId;
                if (!userIdToUse)
                    return undefined;
                const hasFindMethod = typeof adventureInviteService.findInviteBySessionAndUser === 'function';
                let invite;
                if (hasFindMethod) {
                    invite = adventureInviteService.findInviteBySessionAndUser(sessionId, userIdToUse);
                    if (invite && playerSlot && invite.playerSlot !== playerSlot)
                        invite = undefined;
                }
                if (!invite) {
                    const invites = adventureInviteService.getInvites(userIdToUse);
                    invite = playerSlot
                        ? invites.find(inv => inv.sessionId === sessionId && inv.playerSlot === playerSlot && inv.status === 'pending')
                        : invites.find(inv => inv.sessionId === sessionId && inv.inviteeId === userIdToUse && inv.status === 'pending');
                }
                return invite?.id;
            },
            allAdventureInvitesAccepted: (sessionId) => {
                if (!adventureInviteService || !player.userId)
                    return false;
                const sentInvites = adventureInviteService.getSentInvites(player.userId);
                const sessionInvites = sentInvites.filter(inv => inv.sessionId === sessionId);
                if (sessionInvites.length === 0)
                    return true;
                return sessionInvites.every(inv => inv.status === 'accepted');
            },
            playerWaitTimerFinished: () => {
                if (!variableStore)
                    return false;
                const context = {
                    playerId: player.id,
                    characterName: player.characterName,
                    currentSceneId: player.currentScene,
                    userId: player.userId || 'anonymous'
                };
                const finished = variableStore.getVariable('user_player_timer_finished', context);
                return finished === true;
            },
            createEmailActionToken: (config) => {
                if (!emailActionTokenService)
                    return '';
                if (!config || typeof config !== 'object' || Array.isArray(config))
                    return '';
                if (!config.actionType || typeof config.actionType !== 'string' || config.actionType.trim() === '')
                    return '';
                if (!config.actionPayload || typeof config.actionPayload !== 'object' || Array.isArray(config.actionPayload))
                    return '';
                if (!Number.isInteger(config.expiresInSeconds))
                    return '';
                if (config.expiresInSeconds < 60 || config.expiresInSeconds > 7 * 24 * 60 * 60)
                    return '';
                try {
                    return emailActionTokenService.createToken(config, gameSession.id, player.id);
                }
                catch {
                    return '';
                }
            },
            getEmailActionLink: (token) => {
                if (!emailActionTokenService)
                    return '';
                if (!token || typeof token !== 'string' || token.trim() === '')
                    return '';
                try {
                    return emailActionTokenService.buildActionLink(token.trim());
                }
                catch {
                    return '';
                }
            },
            hasPendingEmailActionToken: () => {
                if (!emailActionTokenService)
                    return false;
                try {
                    return emailActionTokenService.hasPendingToken(gameSession.id, player.id);
                }
                catch {
                    return false;
                }
            },
            getPendingEmailActionSummary: () => {
                if (!emailActionTokenService)
                    return '';
                try {
                    return emailActionTokenService.getPendingTokenSummary(gameSession.id, player.id) || '';
                }
                catch {
                    return '';
                }
            }
        };
    }
    /**
     * Create context with mutation functions for effect execution
     */
    static createEffectContext(player, gameSession, tracker, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, emailService, emailActionTokenService, verificationCodeService, analyticsService, effectRuntimeOptions, sharedVariablesService, guestAdventureService) {
        // Start with all condition context functions
        const foreignEntityLookup = effectRuntimeOptions?.foreignEntityLookup;
        const conditionContext = this.createConditionContext(player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, emailService, emailActionTokenService, analyticsService, effectRuntimeOptions?.entitiesService, foreignEntityLookup);
        const inventoryManager = new InventoryManager_1.InventoryManager(gameSession);
        const entitiesService = effectRuntimeOptions?.entitiesService;
        const resolveScheduleEventIdFromStore = (context, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            if (!variableStore)
                return '';
            const fromUser = variableStore.getVariable(userSelectedVar, context);
            if (fromUser !== undefined && fromUser !== null && String(fromUser).trim() !== '') {
                return String(fromUser).trim();
            }
            const fromEvent = variableStore.getVariable(eventIdVar, context);
            if (fromEvent !== undefined && fromEvent !== null && String(fromEvent).trim() !== '') {
                return String(fromEvent).trim();
            }
            return '';
        };
        // Build effect context - use 'result' so schedule helpers can reference mutation functions
        const result = {
            ...conditionContext,
            // Item mutations (backward compatibility)
            addItem: (itemTag) => {
                player.addItem(itemTag);
                tracker.trackItemAdded(itemTag);
            },
            removeItem: (itemTag) => {
                player.removeItem(itemTag);
                tracker.trackItemRemoved(itemTag);
            },
            // Unified inventory mutations - work with any inventory owner
            addItemToInventory: (ownerId, ownerType, itemTag) => {
                inventoryManager.addItem(ownerId, ownerType, itemTag);
                tracker.trackItemAdded(itemTag);
            },
            removeItemFromInventory: (ownerId, ownerType, itemTag) => {
                inventoryManager.removeItem(ownerId, ownerType, itemTag);
                tracker.trackItemRemoved(itemTag);
            },
            // Convenience functions for location inventories
            addItemToLocation: (locationTag, itemTag) => {
                inventoryManager.addLocationItem(locationTag, itemTag);
                tracker.trackItemAdded(itemTag);
            },
            removeItemFromLocation: (locationTag, itemTag) => {
                inventoryManager.removeLocationItem(locationTag, itemTag);
                tracker.trackItemRemoved(itemTag);
            },
            // Stat mutations
            addStat: (statName) => {
                player.addStat(statName);
                tracker.trackStatAdded(statName);
            },
            removeStat: (statName) => {
                player.removeStat(statName);
                tracker.trackStatRemoved(statName);
            },
            startActivity: (activityId) => {
                if (!variableStore) {
                    return;
                }
                const id = (activityId ?? '').toString().trim();
                if (!id) {
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const completed = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, context)));
                const inProgress = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, context)));
                const wasCompleted = completed.delete(id);
                const wasInProgress = inProgress.has(id);
                if (!wasInProgress) {
                    inProgress.add(id);
                }
                if (wasCompleted || !wasInProgress) {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...inProgress]), context, 'string');
                }
                if (wasCompleted) {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...completed]), context, 'string');
                }
                const pendingInitialise = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_PENDING_INITIALISE_VAR, context)));
                const shouldInitialise = pendingInitialise.delete(id);
                if (shouldInitialise) {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_PENDING_INITIALISE_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...pendingInitialise]), context, 'string');
                }
                if (shouldInitialise && adventureManager && effectRuntimeOptions?.activityInitialisingIds) {
                    const adventure = adventureManager.getAdventure(gameSession.adventureId);
                    const def = adventure?.activities?.find((a) => a.id === id);
                    const initScript = def?.initialiseScript?.trim();
                    if (initScript) {
                        const initialisingIds = effectRuntimeOptions.activityInitialisingIds;
                        if (!initialisingIds.has(id)) {
                            initialisingIds.add(id);
                            try {
                                ScriptEvaluator.executeEffects(initScript, player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, emailService, emailActionTokenService, verificationCodeService, analyticsService, undefined, undefined, { activityInitialisingIds: initialisingIds });
                            }
                            finally {
                                initialisingIds.delete(id);
                            }
                        }
                    }
                }
            },
            completeActivity: (activityId) => {
                if (!variableStore) {
                    return;
                }
                const id = (activityId ?? '').toString().trim();
                if (!id) {
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const inProgress = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, context)));
                inProgress.delete(id);
                const completed = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, context)));
                completed.add(id);
                variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...inProgress]), context, 'string');
                variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...completed]), context, 'string');
                const resumeAfterComplete = (0, characterActivityState_1.parseActivityResumeScenes)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, context));
                resumeAfterComplete.delete(id);
                variableStore.setVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, (0, characterActivityState_1.serializeActivityResumeScenes)(resumeAfterComplete), context, 'string');
            },
            /**
             * Clears all activity progress (in progress + completed) for another character in the session
             * (same player id / scene as setVariableForCharacter).
             */
            resetActivityProgressForCharacter: (characterName) => {
                if (!variableStore) {
                    return;
                }
                const cn = (characterName ?? '').toString().trim();
                if (!cn) {
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, cn, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, '[]', context, 'string');
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, '[]', context, 'string');
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, '{}', context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] resetActivityProgressForCharacter: Failed for '${cn}':`, error);
                    throw error;
                }
            },
            /**
             * Removes activity ids from in-progress and completed sets for a character when the activity's
             * type id (from the adventure definition) is listed in typesCsv (comma-separated, trimmed).
             * Unknown ids (not in the merged adventure) are left unchanged. If the adventure has no activity
             * definitions, falls back to a full clear for that character (same as resetActivityProgressForCharacter).
             */
            resetActivityProgressForCharacterTypes: (characterName, typesCsv) => {
                if (!variableStore) {
                    return;
                }
                const cn = (characterName ?? '').toString().trim();
                if (!cn) {
                    return;
                }
                const typeSet = new Set((typesCsv ?? '')
                    .toString()
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean));
                if (typeSet.size === 0) {
                    console.warn('[ScriptEvaluator] resetActivityProgressForCharacterTypes: empty types list; pass e.g. "course,lecture,task" or use resetActivityProgressForCharacter for a full clear');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, cn, player.currentScene, player.userId || 'anonymous');
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                const defs = adventure?.activities;
                if (!defs || defs.length === 0) {
                    try {
                        variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, '[]', context, 'string');
                        variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, '[]', context, 'string');
                        variableStore.setVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, '{}', context, 'string');
                    }
                    catch (error) {
                        console.error(`[ScriptEvaluator] resetActivityProgressForCharacterTypes: full clear failed for '${cn}':`, error);
                        throw error;
                    }
                    return;
                }
                const byId = new Map(defs.map(a => [a.id, a]));
                const shouldRemove = (activityId) => {
                    const def = byId.get(activityId);
                    return Boolean(def && typeSet.has(def.typeId));
                };
                const inProgress = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, context)));
                const completed = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, context)));
                for (const id of inProgress) {
                    if (shouldRemove(id)) {
                        inProgress.delete(id);
                    }
                }
                for (const id of completed) {
                    if (shouldRemove(id)) {
                        completed.delete(id);
                    }
                }
                try {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...inProgress]), context, 'string');
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...completed]), context, 'string');
                    let resumeForTypes = (0, characterActivityState_1.parseActivityResumeScenes)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, context));
                    for (const rid of [...resumeForTypes.keys()]) {
                        if (shouldRemove(rid)) {
                            resumeForTypes.delete(rid);
                        }
                    }
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, (0, characterActivityState_1.serializeActivityResumeScenes)(resumeForTypes), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] resetActivityProgressForCharacterTypes: Failed for '${cn}':`, error);
                    throw error;
                }
            },
            /**
             * Removes specific activity ids from in-progress and completed sets for a character (both sets are updated).
             * Pass comma-separated activity ids, e.g. `task_a,task_b`. Unknown ids are harmless (no-op per id).
             * When {@code includeDescendants} is true, also clears child activities (via Parent) and activities
             * that require any cleared id as Requires Active.
             */
            removeActivitiesFromCharacterProgress: (characterName, activityIdsCsv, includeDescendants) => {
                if (!variableStore) {
                    return;
                }
                const cn = (characterName ?? '').toString().trim();
                if (!cn) {
                    return;
                }
                let idSet = new Set((activityIdsCsv ?? '')
                    .toString()
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean));
                const requestedIds = [...idSet];
                if (includeDescendants && idSet.size > 0) {
                    const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                    const activities = adventure?.activities ?? [];
                    idSet = new Set((0, incompleteActivityChecks_1.collectActivityRedoResetIds)(activities, [...idSet]));
                }
                if (idSet.size === 0) {
                    console.warn('[ScriptEvaluator] removeActivitiesFromCharacterProgress: empty activity id list');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, cn, player.currentScene, player.userId || 'anonymous');
                const inProgress = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, context)));
                const completed = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, context)));
                for (const id of idSet) {
                    inProgress.delete(id);
                    completed.delete(id);
                }
                try {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_IN_PROGRESS_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...inProgress]), context, 'string');
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_COMPLETED_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...completed]), context, 'string');
                    const resumeRemove = (0, characterActivityState_1.parseActivityResumeScenes)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, context));
                    for (const rid of idSet) {
                        resumeRemove.delete(rid);
                    }
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, (0, characterActivityState_1.serializeActivityResumeScenes)(resumeRemove), context, 'string');
                    const pendingInitialise = new Set((0, characterActivityState_1.parseActivityIdJsonArray)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITIES_PENDING_INITIALISE_VAR, context)));
                    for (const rid of requestedIds) {
                        pendingInitialise.add(rid);
                    }
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITIES_PENDING_INITIALISE_VAR, (0, characterActivityState_1.serializeActivityIdJsonArray)([...pendingInitialise]), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] removeActivitiesFromCharacterProgress: Failed for '${cn}':`, error);
                    throw error;
                }
            },
            setActivityResumeScene: (activityId, sceneId) => {
                if (!variableStore) {
                    return;
                }
                const aid = (activityId ?? '').toString().trim();
                const sid = (sceneId ?? '').toString().trim();
                if (!aid || !sid) {
                    return;
                }
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                if (!adventure?.scenes?.some(s => s.id === sid)) {
                    console.warn(`[ScriptEvaluator] setActivityResumeScene: scene '${sid}' not found in adventure '${gameSession.adventureId}'`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const map = (0, characterActivityState_1.parseActivityResumeScenes)(variableStore.getVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, context));
                map.set(aid, sid);
                try {
                    variableStore.setVariable(constants_1.CHARACTER_ACTIVITY_RESUME_SCENES_VAR, (0, characterActivityState_1.serializeActivityResumeScenes)(map), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] setActivityResumeScene: Failed:', error);
                    throw error;
                }
            },
            // Variable API - Synchronous variable setting
            setVariable: (name, value) => {
                if ((0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, name)) {
                    throw new Error(`Variable '${name}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
                }
                if (!variableStore) {
                    console.warn(`setVariable called for '${name}' but no VariableStore available`);
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const variableType = VariableStore_1.VariableStore.inferVariableType(value);
                // Set the variable and let errors propagate
                try {
                    variableStore.setVariable(name, value, context, variableType);
                }
                catch (error) {
                    console.error(`Failed to set variable '${name}':`, error);
                    // Re-throw the error so the caller can handle it
                    throw error;
                }
            },
            setSharedVariable: (name, value) => {
                if (!sharedVariablesService) {
                    throw new Error('setSharedVariable requires SharedVariablesService but none was configured');
                }
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                (0, SharedVariablesService_1.validateSetSharedVariableArgs)(adventure, name, value);
                const userId = (player.userId ?? '').trim();
                if (!userId) {
                    throw new Error('setSharedVariable requires an authenticated user id');
                }
                sharedVariablesService.queueSharedVariableWrite(name, value, {
                    sessionId: gameSession.id,
                    playerId: player.id,
                    userId,
                    adventureId: gameSession.adventureId
                });
            },
            setSharedVariableForUser: (name, value, targetUserId) => {
                if (!sharedVariablesService) {
                    throw new Error('setSharedVariableForUser requires SharedVariablesService but none was configured');
                }
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                (0, SharedVariablesService_1.validateSetSharedVariableArgs)(adventure, name, value);
                const actorUserId = (player.userId ?? '').trim();
                if (!actorUserId) {
                    throw new Error('setSharedVariableForUser requires an authenticated user id');
                }
                const resolvedTarget = String(targetUserId ?? '').trim();
                if (!resolvedTarget) {
                    throw new Error('setSharedVariableForUser requires a target user id');
                }
                sharedVariablesService.queueSharedVariableWrite(name, value, {
                    sessionId: gameSession.id,
                    playerId: player.id,
                    userId: actorUserId,
                    adventureId: gameSession.adventureId,
                    targetUserId: resolvedTarget
                });
            },
            createGuestAdventure: (adventureId, variablesObject, options) => {
                if (!guestAdventureService) {
                    throw new Error('createGuestAdventure requires GuestAdventureService but none was configured');
                }
                const targetAdventureId = (adventureId ?? '').toString().trim();
                if (!targetAdventureId) {
                    throw new Error('createGuestAdventure requires a non-empty adventure id');
                }
                const userId = (player.userId ?? '').trim();
                if (!userId || userId === 'anonymous') {
                    throw new Error('createGuestAdventure requires an authenticated user id');
                }
                if (gameSession.isGuest === true) {
                    throw new Error('createGuestAdventure cannot be called by guest users');
                }
                if (guestAdventureService.isReturnResultOnly(player.id)) {
                    const prior = guestAdventureService.getLastCreateResult(player.id);
                    return prior?.inviteLink ?? '';
                }
                const variables = (0, GuestAdventureService_1.normalizeGuestAdventureVariables)(variablesObject);
                const normalizedOptions = (0, GuestAdventureService_1.normalizeGuestAdventureCreateOptions)(options);
                guestAdventureService.queueGuestAdventureCreate(targetAdventureId, variables, normalizedOptions, {
                    sessionId: gameSession.id,
                    playerId: player.id,
                    userId,
                    sourceSessionId: gameSession.id
                });
                const result = guestAdventureService.getLastCreateResult(player.id);
                return result?.inviteLink ?? '';
            },
            /**
             * Returns metadata from the most recent successful createGuestAdventure flush for this player.
             * Only populated after the backend flushes a pending create (during effect replay).
             */
            getLastGuestAdventureInvite: () => {
                if (!guestAdventureService) {
                    return null;
                }
                const result = guestAdventureService.getLastCreateResult(player.id);
                if (!result) {
                    return null;
                }
                return {
                    inviteId: result.inviteId,
                    inviteLink: result.inviteLink,
                    sessionId: result.sessionId,
                    guestUserId: result.guestUserId
                };
            },
            deleteGuestAdventureInvite: (inviteId) => {
                if (!guestAdventureService) {
                    throw new Error('deleteGuestAdventureInvite requires GuestAdventureService but none was configured');
                }
                if (guestAdventureService.isDeleteResultOnly(player.id)) {
                    return true;
                }
                const trimmedInviteId = (inviteId ?? '').toString().trim();
                if (!trimmedInviteId) {
                    throw new Error('deleteGuestAdventureInvite requires a non-empty invite id');
                }
                const userId = (player.userId ?? '').trim();
                if (!userId || userId === 'anonymous') {
                    throw new Error('deleteGuestAdventureInvite requires an authenticated user id');
                }
                if (gameSession.isGuest === true) {
                    throw new Error('deleteGuestAdventureInvite cannot be called by guest users');
                }
                guestAdventureService.queueGuestAdventureDelete(trimmedInviteId, {
                    sessionId: gameSession.id,
                    playerId: player.id,
                    userId,
                    sourceSessionId: gameSession.id
                });
                return false;
            },
            /**
             * Resets a declared adventure or character variable to its definition default
             * for the current character (character-scoped vars use {@code getPlayerCharacter()}).
             */
            resetVariableToDefault: (name) => {
                if (!variableStore) {
                    console.warn(`resetVariableToDefault called for '${name}' but no VariableStore available`);
                    return;
                }
                const variableName = (name ?? '').toString().trim();
                if (!variableName) {
                    return;
                }
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                if (!adventure) {
                    console.warn(`resetVariableToDefault: adventure '${gameSession.adventureId}' not available for '${variableName}'`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const declaration = (0, variableDeclarationLookup_1.findVariableDeclaration)(adventure, variableName, {
                    characterName: player.characterName
                });
                if (!declaration) {
                    console.warn(`resetVariableToDefault: no resettable declaration for '${variableName}' in adventure '${gameSession.adventureId}'`);
                    return;
                }
                try {
                    variableStore.resetVariableToDeclaredDefault(variableName, context, declaration);
                }
                catch (error) {
                    console.error(`resetVariableToDefault: failed for '${variableName}':`, error);
                    throw error;
                }
            },
            /**
             * Resets a declared character variable to its default for a specific character.
             * Adventure-scoped variables are not reset by this helper (use {@code resetVariableToDefault}).
             */
            resetVariableToDefaultForCharacter: (characterName, name) => {
                if (!variableStore) {
                    console.warn(`resetVariableToDefaultForCharacter called for '${name}' but no VariableStore available`);
                    return;
                }
                const variableName = (name ?? '').toString().trim();
                const targetCharacter = (characterName ?? '').toString().trim();
                if (!variableName || !targetCharacter) {
                    return;
                }
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                if (!adventure) {
                    console.warn(`resetVariableToDefaultForCharacter: adventure '${gameSession.adventureId}' not available for '${variableName}'`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, targetCharacter, player.currentScene, player.userId || 'anonymous');
                const declaration = (0, variableDeclarationLookup_1.findVariableDeclaration)(adventure, variableName, {
                    characterName: targetCharacter
                });
                if (!declaration) {
                    console.warn(`resetVariableToDefaultForCharacter: no resettable declaration for '${variableName}' (character '${targetCharacter}')`);
                    return;
                }
                if (declaration.scope === 'adventure') {
                    console.warn(`resetVariableToDefaultForCharacter: '${variableName}' is adventure-scoped; use resetVariableToDefault instead`);
                    return;
                }
                try {
                    variableStore.resetVariableToDeclaredDefault(variableName, context, declaration);
                }
                catch (error) {
                    console.error(`resetVariableToDefaultForCharacter: failed for '${variableName}' (character '${targetCharacter}'):`, error);
                    throw error;
                }
            },
            /**
             * Adds {@code delta} to a declared numeric variable using the same scope resolution as
             * {@code getVariable} / {@code setVariable} for the current player. Non-finite or missing
             * current values are treated as 0. Non-finite {@code delta} is a no-op (warns).
             */
            addToVariable: (name, delta) => {
                if (!variableStore) {
                    console.warn(`addToVariable called for '${name}' but no VariableStore available`);
                    return;
                }
                const variableName = (name ?? '').toString().trim();
                if (!variableName) {
                    return;
                }
                const addend = Number(delta);
                if (!Number.isFinite(addend)) {
                    console.warn(`addToVariable: non-finite delta for '${variableName}', skipping`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                if ((0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, variableName)) {
                    throw new Error(`Variable '${variableName}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
                }
                const mapMeta = variableStore.getVariableMetadata(variableName, context);
                if (mapMeta?.type === 'character_map') {
                    throw new Error(`Variable '${variableName}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
                }
                const raw = variableStore.getVariable(variableName, context);
                let base;
                if (typeof raw === 'number') {
                    base = raw;
                }
                else if (typeof raw === 'boolean') {
                    base = raw ? 1 : 0;
                }
                else if (raw === undefined || raw === null || raw === '') {
                    base = 0;
                }
                else {
                    base = Number(raw);
                }
                if (!Number.isFinite(base)) {
                    base = 0;
                }
                const next = base + addend;
                const variableType = VariableStore_1.VariableStore.inferVariableType(next);
                try {
                    variableStore.setVariable(variableName, next, context, variableType);
                }
                catch (error) {
                    console.error(`addToVariable: failed to set '${variableName}':`, error);
                    throw error;
                }
            },
            /**
             * Records the current player's checkpoint progress.
             * - Stores last checkpoint tag
             * - Maintains ordered checkpoint history (JSON array string)
             * - Maintains checkpoint-to-scene mapping (JSON object string)
             */
            setCheckpoint: (checkpointTag) => {
                if (!variableStore) {
                    console.warn('setCheckpoint called but no VariableStore available');
                    return;
                }
                const tag = (checkpointTag ?? '').toString().trim();
                if (!tag) {
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const parseHistory = (raw) => {
                    if (typeof raw !== 'string' || raw.trim().length === 0) {
                        return [];
                    }
                    try {
                        const parsed = JSON.parse(raw);
                        if (!Array.isArray(parsed)) {
                            return [];
                        }
                        return parsed.filter((entry) => typeof entry === 'string').map((entry) => entry.trim()).filter(Boolean);
                    }
                    catch {
                        return [];
                    }
                };
                const parseSceneMap = (raw) => {
                    if (typeof raw !== 'string' || raw.trim().length === 0) {
                        return {};
                    }
                    try {
                        const parsed = JSON.parse(raw);
                        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                            return {};
                        }
                        const output = {};
                        for (const [k, v] of Object.entries(parsed)) {
                            if (typeof k === 'string' && typeof v === 'string' && k.trim().length > 0 && v.trim().length > 0) {
                                output[k] = v;
                            }
                        }
                        return output;
                    }
                    catch {
                        return {};
                    }
                };
                const lastRaw = variableStore.getVariable('player_checkpoint_last_tag', context);
                const lastTag = typeof lastRaw === 'string' ? lastRaw.trim() : '';
                const mapRaw = variableStore.getVariable('player_checkpoint_scene_map', context);
                const checkpointSceneMap = parseSceneMap(mapRaw);
                checkpointSceneMap[tag] = player.currentScene;
                try {
                    variableStore.setVariable('player_checkpoint_scene_map', JSON.stringify(checkpointSceneMap), context, 'string', { allowUndeclaredPlayer: true });
                    if (lastTag !== tag) {
                        const historyRaw = variableStore.getVariable('player_checkpoint_history', context);
                        const history = parseHistory(historyRaw);
                        if (history.length === 0 || history[history.length - 1] !== tag) {
                            history.push(tag);
                        }
                        variableStore.setVariable('player_checkpoint_history', JSON.stringify(history), context, 'string', { allowUndeclaredPlayer: true });
                    }
                    variableStore.setVariable('player_checkpoint_last_tag', tag, context, 'string', { allowUndeclaredPlayer: true });
                }
                catch (error) {
                    console.error(`Failed to set checkpoint '${tag}':`, error);
                    throw error;
                }
            },
            /**
             * Resolves the current player's last checkpoint destination and stores it in
             * `player_checkpoint_recover_target_scene` for option target interpolation.
             *
             * Does not move the player directly; scene transition is still handled through
             * the selected option target path.
             */
            recoverToLastCheckpoint: () => {
                if (!variableStore) {
                    console.warn('recoverToLastCheckpoint called but no VariableStore available');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const setRecoverState = (targetScene, errorMessage) => {
                    variableStore.setVariable('player_checkpoint_recover_target_scene', targetScene, context, 'string', { allowUndeclaredPlayer: true });
                    variableStore.setVariable('player_checkpoint_recover_error', errorMessage, context, 'string', { allowUndeclaredPlayer: true });
                    variableStore.setVariable('player_checkpoint_recover_requested_tag', '', context, 'string', { allowUndeclaredPlayer: true });
                };
                const lastTagRaw = variableStore.getVariable('player_checkpoint_last_tag', context);
                const lastTag = typeof lastTagRaw === 'string' ? lastTagRaw.trim() : '';
                if (!lastTag) {
                    setRecoverState(player.currentScene, 'No checkpoint available.');
                    return;
                }
                const mapRaw = variableStore.getVariable('player_checkpoint_scene_map', context);
                let checkpointSceneMap = {};
                if (typeof mapRaw === 'string' && mapRaw.trim().length > 0) {
                    try {
                        const parsed = JSON.parse(mapRaw);
                        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                            checkpointSceneMap = parsed;
                        }
                    }
                    catch {
                        checkpointSceneMap = {};
                    }
                }
                const destinationSceneRaw = checkpointSceneMap[lastTag];
                const destinationScene = typeof destinationSceneRaw === 'string' ? destinationSceneRaw.trim() : '';
                if (!destinationScene) {
                    setRecoverState(player.currentScene, `Checkpoint '${lastTag}' is missing a destination scene.`);
                    return;
                }
                setRecoverState(destinationScene, '');
                variableStore.setVariable('player_checkpoint_recover_requested_tag', lastTag, context, 'string', { allowUndeclaredPlayer: true });
            },
            /**
             * Clears all checkpoint state for the current player.
             * Useful when a challenge section is completed and checkpoint recovery
             * should no longer be offered from previous challenge checkpoints.
             */
            clearCheckpoints: () => {
                if (!variableStore) {
                    console.warn('clearCheckpoints called but no VariableStore available');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                variableStore.setVariable('player_checkpoint_last_tag', '', context, 'string', { allowUndeclaredPlayer: true });
                variableStore.setVariable('player_checkpoint_history', '[]', context, 'string', { allowUndeclaredPlayer: true });
                variableStore.setVariable('player_checkpoint_scene_map', '{}', context, 'string', { allowUndeclaredPlayer: true });
                variableStore.setVariable('player_checkpoint_recover_target_scene', '', context, 'string', { allowUndeclaredPlayer: true });
                variableStore.setVariable('player_checkpoint_recover_error', '', context, 'string', { allowUndeclaredPlayer: true });
                variableStore.setVariable('player_checkpoint_recover_requested_tag', '', context, 'string', { allowUndeclaredPlayer: true });
            },
            /** Set a character variable for a specific character (e.g. "Man", "Woman") */
            setVariableForCharacter: (characterName, name, value) => {
                if (!variableStore) {
                    console.warn(`setVariableForCharacter called for '${name}' but no VariableStore available`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, characterName, player.currentScene, player.userId || 'anonymous');
                const variableType = VariableStore_1.VariableStore.inferVariableType(value);
                try {
                    variableStore.setVariable(name, value, context, variableType);
                }
                catch (error) {
                    console.error(`Failed to set variable '${name}' for character '${characterName}':`, error);
                    throw error;
                }
            },
            /**
             * Adds {@code delta} to a declared numeric variable in another character's scope
             * (same context rules as {@code setVariableForCharacter}).
             */
            addToVariableForCharacter: (characterName, name, delta) => {
                if (!variableStore) {
                    console.warn(`addToVariableForCharacter called for '${name}' but no VariableStore available`);
                    return;
                }
                const variableName = (name ?? '').toString().trim();
                if (!variableName) {
                    return;
                }
                const addend = Number(delta);
                if (!Number.isFinite(addend)) {
                    console.warn(`addToVariableForCharacter: non-finite delta for '${variableName}', skipping`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, characterName, player.currentScene, player.userId || 'anonymous');
                const mapMeta = variableStore.getVariableMetadata(variableName, context);
                if (mapMeta?.type === 'character_map') {
                    throw new Error(`Variable '${variableName}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
                }
                const raw = variableStore.getVariable(variableName, context);
                let base;
                if (typeof raw === 'number') {
                    base = raw;
                }
                else if (typeof raw === 'boolean') {
                    base = raw ? 1 : 0;
                }
                else if (raw === undefined || raw === null || raw === '') {
                    base = 0;
                }
                else {
                    base = Number(raw);
                }
                if (!Number.isFinite(base)) {
                    base = 0;
                }
                const next = base + addend;
                const variableType = VariableStore_1.VariableStore.inferVariableType(next);
                try {
                    variableStore.setVariable(variableName, next, context, variableType);
                }
                catch (error) {
                    console.error(`addToVariableForCharacter: failed to set '${variableName}' for character '${characterName}':`, error);
                    throw error;
                }
            },
            /**
             * Queue toast(s) for the human player(s) controlling {@code characterName}.
             * For an open template name, fans out to every seated instance of that template.
             * No-op when character name or message is empty; does not throw when no player is assigned.
             */
            showToastForCharacter: (characterName, message, type, durationMs) => {
                const trimmed = (characterName ?? '').toString().trim();
                if (!trimmed) {
                    return;
                }
                const adventure = adventureManager?.getAdventure(gameSession.adventureId);
                const template = adventure ? (0, openCharacterRuntime_1.resolveCharacter)(adventure.characters, trimmed) : undefined;
                if (template?.kind === 'open' && !(0, characterInstanceNames_1.isCharacterInstanceName)(trimmed)) {
                    const instances = (0, openCharacterRuntime_1.listInstanceNamesForTemplate)(gameSession, template.name);
                    for (const instanceName of instances) {
                        tracker.trackToast(instanceName, message, type, durationMs);
                    }
                    return;
                }
                tracker.trackToast(trimmed, message, type, durationMs);
            },
            // User portrait helper - validates portrait ID before setting
            setUserPortrait: (portraitId) => {
                if (!variableStore) {
                    console.warn(`setUserPortrait called but no VariableStore available`);
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                // Set the user portrait with validation
                try {
                    variableStore.setUserPortrait(portraitId, context);
                }
                catch (error) {
                    console.error(`Failed to set user portrait '${portraitId}':`, error);
                    // Re-throw the error so the caller can handle it
                    throw error;
                }
            },
            // Stack operations - Push onto stack
            pushStack: (name, value) => {
                if (!variableStore) {
                    console.warn(`pushStack called for '${name}' but no VariableStore available`);
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.pushStack(name, value, context);
                }
                catch (error) {
                    console.error(`Failed to push to stack '${name}':`, error);
                    throw error;
                }
            },
            // Stack operations - Pop from stack
            popStack: (name) => {
                if (!variableStore) {
                    console.warn(`popStack called for '${name}' but no VariableStore available`);
                    return undefined;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    return variableStore.popStack(name, context);
                }
                catch (error) {
                    console.error(`Failed to pop from stack '${name}':`, error);
                    throw error;
                }
            },
            // Stack operations - Peek at top of stack
            peekStack: (name) => {
                if (!variableStore) {
                    console.warn(`peekStack called for '${name}' but no VariableStore available`);
                    return undefined;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                return variableStore.peekStack(name, context);
            },
            setCharacterMapValue: (name, value) => {
                const occupied = gameSession.players.some(p => p.id !== player.id && p.currentCharacter === player.characterName);
                if (occupied) {
                    throw new Error(`Cannot write character_map '${name}': another player is using character '${player.characterName}'`);
                }
                const entityMap = (0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, name);
                if (entityMap) {
                    try {
                        entitiesService.setEntityCharacterMapSlot(entityMap.uuid, name, player.characterName, String(value ?? ''));
                    }
                    catch (error) {
                        console.error(`Failed to set character_map '${name}':`, error);
                        throw error;
                    }
                    return;
                }
                if (!variableStore) {
                    console.warn(`setCharacterMapValue called for '${name}' but no VariableStore available`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.setCharacterMapSlot(name, String(value ?? ''), context);
                }
                catch (error) {
                    console.error(`Failed to set character_map '${name}':`, error);
                    throw error;
                }
            },
            clearCharacterMapValue: (name) => {
                const occupied = gameSession.players.some(p => p.id !== player.id && p.currentCharacter === player.characterName);
                if (occupied) {
                    throw new Error(`Cannot write character_map '${name}': another player is using character '${player.characterName}'`);
                }
                const entityMap = (0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, name);
                if (entityMap) {
                    try {
                        entitiesService.setEntityCharacterMapSlot(entityMap.uuid, name, player.characterName, '', { clear: true });
                    }
                    catch (error) {
                        console.error(`Failed to clear character_map '${name}':`, error);
                        throw error;
                    }
                    return;
                }
                if (!variableStore) {
                    console.warn(`clearCharacterMapValue called for '${name}' but no VariableStore available`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.setCharacterMapSlot(name, '', context, { clear: true });
                }
                catch (error) {
                    console.error(`Failed to clear character_map '${name}':`, error);
                    throw error;
                }
            },
            resetCharacterMap: (name) => {
                const entityMap = (0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, name);
                if (entityMap) {
                    try {
                        entitiesService.resetEntityCharacterMap(entityMap.uuid, name);
                    }
                    catch (error) {
                        console.error(`Failed to reset character_map '${name}':`, error);
                        throw error;
                    }
                    return;
                }
                if (!variableStore) {
                    console.warn(`resetCharacterMap called for '${name}' but no VariableStore available`);
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.resetCharacterMap(name, context);
                }
                catch (error) {
                    console.error(`Failed to reset character_map '${name}':`, error);
                    throw error;
                }
            },
            // Timer effect functions
            initialiseTimer: (timerVar, mode) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const timerMode = mode && ['blind', 'stopwatch', 'max', 'min', 'deadline'].includes(mode) ? mode : undefined;
                const state = (0, TimerState_1.createInitialTimerState)(timerMode);
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(state), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] initialiseTimer: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            startTimer: (timerVar) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                if (state.startedAt !== null && state.stoppedAt === null)
                    return;
                const now = ScriptEvaluator.resolveNowMs();
                const hasPreCountdown = state.preCountdownSeconds !== null && state.preCountdownSeconds > 0;
                const next = hasPreCountdown
                    ? {
                        ...state,
                        armedAt: now,
                        startedAt: now + state.preCountdownSeconds * 1000,
                        stoppedAt: null
                    }
                    : {
                        ...state,
                        armedAt: null,
                        startedAt: now,
                        stoppedAt: null
                    };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] startTimer: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            stopTimer: (timerVar) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                if (state.startedAt === null)
                    return;
                const next = {
                    ...state,
                    stoppedAt: ScriptEvaluator.resolveNowMs()
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] stopTimer: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setMaxTime: (timerVar, maxTimeInSeconds) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const seconds = Number(maxTimeInSeconds);
                if (!Number.isFinite(seconds) || seconds < 0)
                    return;
                const next = {
                    ...state,
                    mode: 'max',
                    maxSeconds: seconds
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setMaxTime: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setMinTime: (timerVar, minTimeInSeconds) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const seconds = Number(minTimeInSeconds);
                if (!Number.isFinite(seconds) || seconds < 0)
                    return;
                const next = {
                    ...state,
                    mode: 'min',
                    minSeconds: seconds
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setMinTime: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setTimerDeadline: (timerVar, targetEpochMs) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const target = Number(targetEpochMs);
                if (!Number.isFinite(target) || target <= 0)
                    return;
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    mode: 'deadline',
                    targetAt: target,
                    startedAt: state.startedAt ?? ScriptEvaluator.resolveNowMs(),
                    stoppedAt: null,
                    maxSeconds: null,
                    minSeconds: null
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setTimerDeadline: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setTimerDeadlineToLocalMidnight: (timerVar) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const offset = (0, timerDeadlineHelpers_1.resolvePlayerTimezoneOffsetMinutes)(player, variableStore);
                const target = (0, dateTime_1.localMidnightUtcMsFromOffset)(offset);
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    mode: 'deadline',
                    targetAt: target,
                    startedAt: ScriptEvaluator.resolveNowMs(),
                    stoppedAt: null,
                    maxSeconds: null,
                    minSeconds: null
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setTimerDeadlineToLocalMidnight: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setTimerDeadlineToLocalHHMM: (timerVar, hhmm) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const offset = (0, timerDeadlineHelpers_1.resolvePlayerTimezoneOffsetMinutes)(player, variableStore);
                const target = (0, dateTime_1.localHHMMToNextUtcMs)(hhmm, offset);
                if (target === null)
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    mode: 'deadline',
                    targetAt: target,
                    startedAt: ScriptEvaluator.resolveNowMs(),
                    stoppedAt: null,
                    maxSeconds: null,
                    minSeconds: null
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setTimerDeadlineToLocalHHMM: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            /** Move startedAt earlier so elapsed increases (for testing long durations). No-op if not running. */
            advanceTimerElapsed: (timerVar, seconds) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                if (state.startedAt === null || state.stoppedAt !== null)
                    return;
                const delta = Number(seconds);
                if (!Number.isFinite(delta) || delta <= 0)
                    return;
                const next = {
                    ...state,
                    startedAt: state.startedAt - delta * 1000
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] advanceTimerElapsed: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setTimerSoundCues: (timerVar, cues) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    soundCues: (0, TimerState_1.normaliseSoundCues)(cues)
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setTimerSoundCues: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            addTimerSound: (timerVar, atSeconds, audioId, anchor) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const cue = (0, TimerState_1.normaliseSoundCue)({
                    audioId,
                    atSeconds,
                    anchor: anchor ?? 'elapsed'
                });
                if (!cue)
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    soundCues: [...state.soundCues, cue]
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] addTimerSound: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            clearTimerSoundCues: (timerVar) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    soundCues: []
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] clearTimerSoundCues: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            setTimerPreCountdown: (timerVar, seconds, audioId) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const parsedSeconds = Number(seconds);
                if (!Number.isFinite(parsedSeconds) || parsedSeconds < 0)
                    return;
                const trimmedAudio = typeof audioId === 'string' && audioId.trim().length > 0 ? audioId.trim() : null;
                const next = {
                    ...state,
                    preCountdownSeconds: parsedSeconds > 0 ? parsedSeconds : null,
                    preCountdownAudioId: parsedSeconds > 0 ? trimmedAudio : null
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] setTimerPreCountdown: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            clearTimerPreCountdown: (timerVar) => {
                if (!variableStore || !timerVar || typeof timerVar !== 'string')
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(timerVar, context);
                const state = (0, TimerState_1.parseTimerState)(raw === undefined || raw === null ? undefined : String(raw));
                const next = {
                    ...state,
                    preCountdownSeconds: null,
                    preCountdownAudioId: null,
                    armedAt: null
                };
                try {
                    variableStore.setVariable(timerVar, (0, TimerState_1.serialiseTimerState)(next), context, 'string');
                }
                catch (error) {
                    console.error(`[ScriptEvaluator] clearTimerPreCountdown: Failed to set variable '${timerVar}':`, error);
                    throw error;
                }
            },
            // Friend effect functions (mutations) - fire-and-forget
            // Always use player.userId for user-specific operations
            sendFriendRequest: (email) => {
                console.log(`[ScriptEvaluator] DEBUG: sendFriendRequest called - email: ${email}, player.userId: ${player.userId}, friendsService available: ${!!friendsService}`);
                if (!friendsService || !player.userId) {
                    console.error('[ScriptEvaluator] sendFriendRequest: FriendsService not available or user not authenticated');
                    return;
                }
                // Fire-and-forget - errors are logged by FriendsService
                console.log(`[ScriptEvaluator] DEBUG: Calling friendsService.sendFriendRequest with userId: ${player.userId}, email: ${email}`);
                friendsService.sendFriendRequest(player.userId, email);
                console.log(`[ScriptEvaluator] DEBUG: friendsService.sendFriendRequest call completed (fire-and-forget)`);
            },
            acceptFriendRequest: (requestId) => {
                console.log(`[ScriptEvaluator] ACCEPT_FRIEND_REQUEST: Called with requestId=${requestId}, player.userId=${player.userId}`);
                if (!friendsService || !player.userId) {
                    console.error('[ScriptEvaluator] ACCEPT_FRIEND_REQUEST: FriendsService not available or user not authenticated');
                    return;
                }
                console.log(`[ScriptEvaluator] ACCEPT_FRIEND_REQUEST: Calling respondToFriendRequest with requestId=${requestId}, userId=${player.userId}, accepted=true`);
                // Fire-and-forget - errors are logged by FriendsService
                friendsService.respondToFriendRequest(requestId, player.userId, true);
                console.log(`[ScriptEvaluator] ACCEPT_FRIEND_REQUEST: respondToFriendRequest call completed (fire-and-forget)`);
            },
            rejectFriendRequest: (requestId) => {
                if (!friendsService || !player.userId) {
                    console.error('[ScriptEvaluator] rejectFriendRequest: FriendsService not available or user not authenticated');
                    return;
                }
                // Fire-and-forget - errors are logged by FriendsService
                friendsService.respondToFriendRequest(requestId, player.userId, false);
            },
            removeFriend: (friendId) => {
                if (!friendsService || !player.userId) {
                    console.error('[ScriptEvaluator] removeFriend: FriendsService not available or user not authenticated');
                    return;
                }
                // Fire-and-forget - errors are logged by FriendsService
                friendsService.removeFriend(player.userId, friendId);
            },
            // Adventure invite effect functions (mutations) - fire-and-forget
            // Always use player.userId for user-specific operations
            sendAdventureInvite: (friendId, sessionId, playerSlot) => {
                if (!adventureInviteService || !player.userId) {
                    console.error('[ScriptEvaluator] sendAdventureInvite: AdventureInviteService not available or user not authenticated');
                    return;
                }
                // Fire-and-forget - errors are logged by AdventureInviteService
                adventureInviteService.sendInvite(player.userId, friendId, sessionId, playerSlot);
            },
            acceptAdventureInvite: (inviteId) => {
                // Always use player.userId for user-specific operations
                const userIdToUse = player.userId;
                console.log(`[ScriptEvaluator] acceptAdventureInvite: Called with inviteId=${inviteId}`);
                console.log(`[ScriptEvaluator] acceptAdventureInvite: Using userId=${userIdToUse || 'none'} (from player)`);
                if (!adventureInviteService || !userIdToUse) {
                    console.error(`[ScriptEvaluator] acceptAdventureInvite: AdventureInviteService not available or user not authenticated (userId=${userIdToUse})`);
                    return;
                }
                if (!inviteId) {
                    console.error('[ScriptEvaluator] acceptAdventureInvite: inviteId is empty or undefined');
                    return;
                }
                // Fire-and-forget - errors are logged by AdventureInviteService
                // Note: This only updates the invite status. The actual session join happens via JOIN_ADVENTURE target syntax.
                console.log(`[ScriptEvaluator] acceptAdventureInvite: Calling respondToInvite with inviteId=${inviteId}, userId=${userIdToUse}, accept=true`);
                adventureInviteService.respondToInvite(inviteId, userIdToUse, true);
                console.log(`[ScriptEvaluator] acceptAdventureInvite: respondToInvite call completed`);
            },
            rejectAdventureInvite: (inviteId) => {
                console.log(`[ScriptEvaluator] rejectAdventureInvite: Called with inviteId=${inviteId}`);
                // Always use player.userId for user-specific operations
                const userIdToUse = player.userId;
                console.log(`[ScriptEvaluator] rejectAdventureInvite: userIdToUse=${userIdToUse}, adventureInviteService=${!!adventureInviteService}`);
                if (!adventureInviteService || !userIdToUse) {
                    console.error(`[ScriptEvaluator] rejectAdventureInvite: AdventureInviteService not available or user not authenticated (userId=${userIdToUse})`);
                    return;
                }
                // Fire-and-forget - errors are logged by AdventureInviteService
                console.log(`[ScriptEvaluator] rejectAdventureInvite: Calling respondToInvite with inviteId=${inviteId}, userId=${userIdToUse}, accepted=false`);
                adventureInviteService.respondToInvite(inviteId, userIdToUse, false);
                console.log(`[ScriptEvaluator] rejectAdventureInvite: respondToInvite call completed (fire-and-forget)`);
            },
            cancelAdventureInvite: (inviteId) => {
                // Always use player.userId for user-specific operations
                if (!adventureInviteService || !player.userId) {
                    console.error('[ScriptEvaluator] cancelAdventureInvite: AdventureInviteService not available or user not authenticated');
                    return;
                }
                // Fire-and-forget - errors are logged by AdventureInviteService
                adventureInviteService.cancelInvite(inviteId, player.userId);
            },
            // Character switching effect
            setCurrentCharacter: (characterName) => {
                if (!adventureManager) {
                    console.error('[ScriptEvaluator] setCurrentCharacter: AdventureManager not available');
                    return;
                }
                const adventure = adventureManager.getAdventure(gameSession.adventureId);
                if (!adventure) {
                    console.error('[ScriptEvaluator] setCurrentCharacter: Adventure not found');
                    return;
                }
                const resolved = (0, openCharacterRuntime_1.resolveSetCurrentCharacterName)(gameSession, adventure.characters, player, characterName);
                if ('error' in resolved) {
                    console.error(`[ScriptEvaluator] setCurrentCharacter: ${resolved.error}`);
                    return;
                }
                const boundName = resolved.instanceOrFixedName;
                // Set the character on the player
                player.setCurrentCharacter(boundName);
                // Seed character-scoped defaults for open instances (sync; before later setVariable in same effect)
                {
                    const charVars = new Map();
                    if (adventure.characterVariables) {
                        for (const variable of adventure.characterVariables) {
                            charVars.set(variable.name, {
                                name: variable.name,
                                value: variable.defaultValue,
                                type: variable.type
                            });
                        }
                    }
                    if (resolved.template.variables) {
                        for (const variable of resolved.template.variables) {
                            charVars.set(variable.name, {
                                name: variable.name,
                                value: variable.defaultValue,
                                type: variable.type
                            });
                        }
                    }
                    if (variableStore) {
                        variableStore.ensureCharacterVariableDefaults(resolved.template.name, charVars);
                        if ((0, characterInstanceNames_1.isCharacterInstanceName)(boundName)) {
                            variableStore.ensureCharacterVariableDefaults(boundName, charVars);
                        }
                    }
                }
                // Track last userId for this character (character-scoped)
                if (variableStore && player.userId) {
                    const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId);
                    try {
                        variableStore.setVariable('character_last_user_id', player.userId, context, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] setCurrentCharacter: Failed to set character_last_user_id:', error);
                    }
                }
                // Track the character change
                tracker.trackCharacterChanged(boundName);
            },
            // Async operation functions - fire-and-forget
            registerUser: (email, username, password) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] registerUser: VariableStore not available');
                    return;
                }
                // Validate parameters
                if (!email || typeof email !== 'string' || email.trim().length === 0) {
                    console.error('[ScriptEvaluator] registerUser: Invalid email parameter');
                    return;
                }
                if (!username || typeof username !== 'string' || username.trim().length === 0) {
                    console.error('[ScriptEvaluator] registerUser: Invalid username parameter');
                    return;
                }
                if (!password || typeof password !== 'string' || password.trim().length === 0) {
                    console.error('[ScriptEvaluator] registerUser: Invalid password parameter');
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                // Check if registration is already in progress
                const currentStatus = variableStore.getVariable('user_registration_process_status', context);
                if (currentStatus === 'inprogress') {
                    console.warn('[ScriptEvaluator] registerUser: Registration already in progress, ignoring duplicate call');
                    return;
                }
                // Set status to 'inprogress' immediately
                try {
                    variableStore.setVariable('user_registration_process_status', 'inprogress', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] registerUser: Failed to set status variable:', error);
                    return;
                }
                // Call backend async registration (fire-and-forget)
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] registerUser: AsyncOperationService not available, registration will not be processed');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService.registerUserAsync(email.trim(), username.trim(), password, gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined).catch((error) => {
                    console.error('[ScriptEvaluator] registerUser: Async operation failed:', error);
                });
            },
            checkEmailAvailable: (email) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] checkEmailAvailable: VariableStore not available');
                    return;
                }
                // Validate parameters
                if (!email || typeof email !== 'string' || email.trim().length === 0) {
                    console.error('[ScriptEvaluator] checkEmailAvailable: Invalid email parameter');
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                // Always redo the check - handles stale 'inprogress' from server restart or previous session
                // Set status to 'inprogress' immediately
                try {
                    variableStore.setVariable('user_email_check_status', 'inprogress', context, 'string');
                    variableStore.setVariable('user_email_check_error', '', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] checkEmailAvailable: Failed to set status variable:', error);
                    return;
                }
                // Call backend async email check (fire-and-forget)
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] checkEmailAvailable: AsyncOperationService not available, email check will not be processed');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService.checkEmailAvailableAsync(email.trim(), gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined).catch((error) => {
                    console.error('[ScriptEvaluator] checkEmailAvailable: Async operation failed:', error);
                });
            },
            /**
             * Password-reset existence validation.
             * Sets:
             * - user_password_reset_email_check_status: inprogress, exists, not_found, error
             * - user_password_reset_email_check_error
             * - user_password_reset_user_id: target user id when exists
             */
            checkPasswordResetUserExists: (email) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] checkPasswordResetUserExists: VariableStore not available');
                    return;
                }
                if (!email || typeof email !== 'string' || email.trim().length === 0) {
                    console.error('[ScriptEvaluator] checkPasswordResetUserExists: Invalid email parameter');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.setVariable('user_password_reset_email_check_status', 'inprogress', context, 'string');
                    variableStore.setVariable('user_password_reset_email_check_error', '', context, 'string');
                    variableStore.setVariable('user_password_reset_user_id', '', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] checkPasswordResetUserExists: Failed to set initial status:', error);
                    return;
                }
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] checkPasswordResetUserExists: AsyncOperationService not available');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService
                    .checkPasswordResetUserExistsAsync(email.trim(), gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined)
                    .catch((error) => {
                    console.error('[ScriptEvaluator] checkPasswordResetUserExists: Async operation failed:', error);
                });
            },
            /**
             * Update password for a specific user id (used after successful password-reset verification).
             * Sets:
             * - user_password_reset_process_status: inprogress, success, error
             * - user_password_reset_error
             */
            updatePasswordForUser: (userIdToUpdate, newPassword) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] updatePasswordForUser: VariableStore not available');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const setError = (message) => {
                    try {
                        variableStore.setVariable('user_password_reset_process_status', 'error', context, 'string');
                        variableStore.setVariable('user_password_reset_error', message, context, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] updatePasswordForUser: Failed to set error status:', error);
                    }
                };
                if (!userIdToUpdate || typeof userIdToUpdate !== 'string' || userIdToUpdate.trim().length === 0) {
                    console.error('[ScriptEvaluator] updatePasswordForUser: Missing userIdToUpdate');
                    setError('Password reset failed. Please try again.');
                    return;
                }
                if (!newPassword || typeof newPassword !== 'string' || newPassword.trim().length === 0) {
                    console.error('[ScriptEvaluator] updatePasswordForUser: Invalid newPassword parameter');
                    setError('Please enter your new password.');
                    return;
                }
                try {
                    variableStore.setVariable('user_password_reset_process_status', 'inprogress', context, 'string');
                    variableStore.setVariable('user_password_reset_error', '', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] updatePasswordForUser: Failed to set initial status:', error);
                    return;
                }
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] updatePasswordForUser: AsyncOperationService not available');
                    setError('Password reset failed. Please try again.');
                    return;
                }
                const actingUserId = player.userId || 'anonymous';
                asyncOperationService
                    .updatePasswordForUserAsync(userIdToUpdate.trim(), newPassword, gameSession.id, player.id, actingUserId !== 'anonymous' ? actingUserId : undefined)
                    .catch((error) => {
                    console.error('[ScriptEvaluator] updatePasswordForUser: Async operation failed:', error);
                });
            },
            loginUser: (email, password) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] loginUser: VariableStore not available');
                    return;
                }
                // Validate parameters
                if (!email || typeof email !== 'string' || email.trim().length === 0) {
                    console.error('[ScriptEvaluator] loginUser: Invalid email parameter');
                    return;
                }
                if (!password || typeof password !== 'string' || password.trim().length === 0) {
                    console.error('[ScriptEvaluator] loginUser: Invalid password parameter');
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                // Check if login is already in progress
                const currentStatus = variableStore.getVariable('user_login_process_status', context);
                if (currentStatus === 'inprogress') {
                    console.warn('[ScriptEvaluator] loginUser: Login already in progress, ignoring duplicate call');
                    return;
                }
                // Set status to 'inprogress' immediately
                try {
                    variableStore.setVariable('user_login_process_status', 'inprogress', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] loginUser: Failed to set status variable:', error);
                    return;
                }
                // Call backend async login (fire-and-forget)
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] loginUser: AsyncOperationService not available, login will not be processed');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService.loginUserAsync(email.trim(), password, gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined).catch((error) => {
                    console.error('[ScriptEvaluator] loginUser: Async operation failed:', error);
                });
            },
            startPlayerWaitTimer: (milliseconds) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] startPlayerWaitTimer: VariableStore not available');
                    return;
                }
                // Validate parameter
                if (typeof milliseconds !== 'number' || milliseconds <= 0 || !Number.isFinite(milliseconds)) {
                    console.error('[ScriptEvaluator] startPlayerWaitTimer: Invalid milliseconds parameter (must be positive number)');
                    return;
                }
                // Always use player.userId for user-specific variable operations
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                // Set timer finished to false
                try {
                    variableStore.setVariable('user_player_timer_finished', false, context, 'boolean');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] startPlayerWaitTimer: Failed to set timer variable:', error);
                    return;
                }
                // Call backend to start timer (fire-and-forget)
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] startPlayerWaitTimer: AsyncOperationService not available, timer will not be started');
                    return;
                }
                asyncOperationService.startPlayerWaitTimer(milliseconds, gameSession.id, player.id).catch((error) => {
                    console.error('[ScriptEvaluator] startPlayerWaitTimer: Async operation failed:', error);
                });
            },
            setAnalyticsDisplayName: (displayName) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] setAnalyticsDisplayName: VariableStore not available');
                    return;
                }
                const trimmed = (displayName ?? '').toString().trim();
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const setError = (message) => {
                    try {
                        variableStore.setVariable('user_analytics_alias_update_status', 'error', context, 'string');
                        variableStore.setVariable('user_analytics_alias_update_error', message, context, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] setAnalyticsDisplayName: Failed to set error variables:', error);
                    }
                };
                if (!trimmed) {
                    setError('Please enter an alias.');
                    return;
                }
                try {
                    variableStore.setVariable('user_analytics_alias_update_status', 'inprogress', context, 'string');
                    variableStore.setVariable('user_analytics_alias_update_error', '', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] setAnalyticsDisplayName: Failed to set initial status:', error);
                    return;
                }
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] setAnalyticsDisplayName: AsyncOperationService not available');
                    setError('Alias update service is unavailable right now.');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService
                    .setAnalyticsDisplayNameAsync(trimmed, gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined)
                    .catch((error) => {
                    console.error('[ScriptEvaluator] setAnalyticsDisplayName: Async operation failed:', error);
                });
            },
            submitGeneralFeedback: (feedbackType, title, content) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] submitGeneralFeedback: VariableStore not available');
                    return;
                }
                const typeValue = (feedbackType ?? '').toString().trim();
                const titleValue = (title ?? '').toString().trim();
                const contentValue = (content ?? '').toString().trim();
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const setError = (message) => {
                    try {
                        variableStore.setVariable('user_feedback_submit_status', 'error', context, 'string');
                        variableStore.setVariable('user_feedback_submit_error', message, context, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] submitGeneralFeedback: Failed to set error variables:', error);
                    }
                };
                if (!typeValue || !titleValue || !contentValue) {
                    setError('Please choose a type, title, and feedback content.');
                    return;
                }
                try {
                    variableStore.setVariable('user_feedback_submit_status', 'inprogress', context, 'string');
                    variableStore.setVariable('user_feedback_submit_error', '', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] submitGeneralFeedback: Failed to set initial status:', error);
                    return;
                }
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] submitGeneralFeedback: AsyncOperationService not available');
                    setError('Feedback service is unavailable right now.');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService
                    .submitGeneralFeedbackAsync(typeValue, titleValue, contentValue, gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined)
                    .catch((error) => {
                    console.error('[ScriptEvaluator] submitGeneralFeedback: Async operation failed:', error);
                });
            },
            submitAdventureFeedback: (feedbackType, title, content, adventureId, adventureName, sceneId) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] submitAdventureFeedback: VariableStore not available');
                    return;
                }
                const typeValue = (feedbackType ?? '').toString().trim();
                const titleValue = (title ?? '').toString().trim();
                const contentValue = (content ?? '').toString().trim();
                const adventureIdValue = (adventureId ?? '').toString().trim();
                const adventureNameValue = (adventureName ?? '').toString().trim();
                const sceneIdValue = (sceneId ?? '').toString().trim();
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const setError = (message) => {
                    try {
                        variableStore.setVariable('user_feedback_submit_status', 'error', context, 'string');
                        variableStore.setVariable('user_feedback_submit_error', message, context, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] submitAdventureFeedback: Failed to set error variables:', error);
                    }
                };
                if (!typeValue || !titleValue || !contentValue) {
                    setError('Please choose a type, title, and feedback content.');
                    return;
                }
                if (!adventureIdValue || !adventureNameValue || !sceneIdValue) {
                    setError('Adventure id, adventure name, and scene are required for adventure feedback.');
                    return;
                }
                try {
                    variableStore.setVariable('user_feedback_submit_status', 'inprogress', context, 'string');
                    variableStore.setVariable('user_feedback_submit_error', '', context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] submitAdventureFeedback: Failed to set initial status:', error);
                    return;
                }
                if (!asyncOperationService) {
                    console.warn('[ScriptEvaluator] submitAdventureFeedback: AsyncOperationService not available');
                    setError('Feedback service is unavailable right now.');
                    return;
                }
                const userId = player.userId || 'anonymous';
                asyncOperationService
                    .submitAdventureFeedbackAsync(typeValue, titleValue, contentValue, adventureIdValue, adventureNameValue, sceneIdValue, gameSession.id, player.id, userId !== 'anonymous' ? userId : undefined)
                    .catch((error) => {
                    console.error('[ScriptEvaluator] submitAdventureFeedback: Async operation failed:', error);
                });
            },
            createEvent: (scheduleVar, title, location, durationMinutes, requiredCharacters, startScene, requiresRealWorldLocation) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] createEvent: VariableStore not available');
                    return undefined;
                }
                if (!scheduleVar || typeof scheduleVar !== 'string') {
                    console.error('[ScriptEvaluator] createEvent: Invalid scheduleVar');
                    return undefined;
                }
                if (!title || typeof title !== 'string') {
                    console.error('[ScriptEvaluator] createEvent: Invalid title');
                    return undefined;
                }
                if (!location || typeof location !== 'string') {
                    console.error('[ScriptEvaluator] createEvent: Invalid location');
                    return undefined;
                }
                if (!Array.isArray(requiredCharacters) || requiredCharacters.length === 0) {
                    console.error('[ScriptEvaluator] createEvent: Invalid requiredCharacters');
                    return undefined;
                }
                const MAX_EVENTS = 50;
                const MAX_TITLE_LENGTH = 120;
                const MAX_REQUIRED_CHARACTERS = 20;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const readSchedule = () => {
                    const raw = variableStore.getVariable(scheduleVar, context);
                    if (raw === undefined || raw === null || raw === '') {
                        return { events: [] };
                    }
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        const events = Array.isArray(parsed?.events) ? parsed.events : [];
                        return { events };
                    }
                    catch {
                        return { events: [] };
                    }
                };
                const schedule = readSchedule();
                const trimmedTitle = title.trim().slice(0, MAX_TITLE_LENGTH);
                const required = requiredCharacters.slice(0, MAX_REQUIRED_CHARACTERS).map(String);
                const eventId = `evt_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
                const trimmedStartScene = startScene && typeof startScene === 'string' ? startScene.trim() : 'event_start';
                schedule.events.push({
                    id: eventId,
                    title: trimmedTitle,
                    location: location.trim(),
                    durationMinutes: Number(durationMinutes),
                    requiredCharacters: required,
                    startScene: trimmedStartScene,
                    requiresRealWorldLocation: requiresRealWorldLocation === true,
                    state: 'proposed',
                    proposedTimes: [],
                    scheduledTimeEpochSeconds: null
                });
                if (schedule.events.length > MAX_EVENTS) {
                    schedule.events = schedule.events.slice(-MAX_EVENTS);
                }
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] createEvent: Failed to set variable:', error);
                    throw error;
                }
                return eventId;
            },
            proposeEventTime: (scheduleVar, eventId, startTimeEpochSeconds, realWorldLocation) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] proposeEventTime: VariableStore not available');
                    return;
                }
                if (!scheduleVar || !eventId || !Number.isFinite(startTimeEpochSeconds)) {
                    console.error('[ScriptEvaluator] proposeEventTime: Invalid parameters');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const event = schedule.events.find((e) => e?.id === eventId);
                if (!event) {
                    console.error(`[ScriptEvaluator] proposeEventTime: Event '${eventId}' not found`);
                    return;
                }
                const proposer = player.characterName;
                const normalizedLocation = (0, scheduleEventHelpers_1.resolveRealWorldLocationForEvent)(event, realWorldLocation) ?? '';
                const existing = Array.isArray(event.proposedTimes) ? event.proposedTimes : [];
                const match = (0, scheduleEventHelpers_1.findProposedTimeEntry)(existing, startTimeEpochSeconds, normalizedLocation);
                if (match) {
                    let acceptedList = Array.isArray(match.acceptedBy) ? match.acceptedBy : [];
                    if (!acceptedList.includes(proposer)) {
                        acceptedList = [...acceptedList, proposer];
                    }
                    match.acceptedBy = acceptedList;
                }
                else {
                    const proposal = {
                        startTimeEpochSeconds: Number(startTimeEpochSeconds),
                        proposedBy: proposer,
                        acceptedBy: [proposer]
                    };
                    if (normalizedLocation) {
                        proposal.realWorldLocation = normalizedLocation;
                    }
                    existing.push(proposal);
                }
                event.proposedTimes = existing;
                // If proposer is always counted as accepting, check if all required have accepted and schedule
                const acceptedBy = Array.isArray(event.requiredCharacters) ? event.requiredCharacters : [];
                const candidate = (0, scheduleEventHelpers_1.findProposedTimeEntry)(existing, startTimeEpochSeconds, normalizedLocation);
                const allAccepted = candidate && acceptedBy.every((name) => Array.isArray(candidate.acceptedBy) && candidate.acceptedBy.includes(name));
                if (allAccepted) {
                    (0, scheduleEventHelpers_1.applyScheduledTimeToEvent)(event, startTimeEpochSeconds, normalizedLocation || candidate.realWorldLocation);
                }
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] proposeEventTime: Failed to set variable:', error);
                    throw error;
                }
            },
            acceptEventTime: (scheduleVar, eventId, startTimeEpochSeconds, realWorldLocation) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] acceptEventTime: VariableStore not available');
                    return;
                }
                if (!scheduleVar || !eventId || !Number.isFinite(startTimeEpochSeconds)) {
                    console.error('[ScriptEvaluator] acceptEventTime: Invalid parameters');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const event = schedule.events.find((e) => e?.id === eventId);
                if (!event) {
                    console.error(`[ScriptEvaluator] acceptEventTime: Event '${eventId}' not found`);
                    return;
                }
                const acceptedBy = Array.isArray(event.requiredCharacters) ? event.requiredCharacters : [];
                const locationForMatch = (0, scheduleEventHelpers_1.resolveRealWorldLocationForEvent)(event, realWorldLocation);
                const candidate = (0, scheduleEventHelpers_1.findProposedTimeEntry)(event.proposedTimes, startTimeEpochSeconds, locationForMatch);
                if (!candidate) {
                    console.error('[ScriptEvaluator] acceptEventTime: Proposed time not found');
                    return;
                }
                let candidateAccepted = Array.isArray(candidate.acceptedBy) ? candidate.acceptedBy : [];
                if (!candidateAccepted.includes(player.characterName)) {
                    candidateAccepted = [...candidateAccepted, player.characterName];
                }
                candidate.acceptedBy = candidateAccepted;
                const allAccepted = acceptedBy.every((name) => candidateAccepted.includes(name));
                if (allAccepted) {
                    (0, scheduleEventHelpers_1.applyScheduledTimeToEvent)(event, startTimeEpochSeconds, locationForMatch || candidate.realWorldLocation);
                }
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] acceptEventTime: Failed to set variable:', error);
                    throw error;
                }
            },
            finishEventForCharacter: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                if (!variableStore)
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const rawId = resolveScheduleEventIdFromStore(context, eventIdVar, userSelectedVar);
                const eventId = rawId ? String(rawId).trim() : '';
                if (!eventId)
                    return;
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const eventIndex = schedule.events.findIndex((e) => e?.id === eventId);
                if (eventIndex === -1)
                    return; // Already removed
                const event = schedule.events[eventIndex];
                if (!Array.isArray(event.completedBy))
                    event.completedBy = [];
                if (!event.completedBy.includes(player.characterName)) {
                    event.completedBy.push(player.characterName);
                }
                const allDone = Array.isArray(event.requiredCharacters) &&
                    event.requiredCharacters.length > 0 &&
                    event.requiredCharacters.every((c) => event.completedBy.includes(c));
                if (allDone) {
                    schedule.events.splice(eventIndex, 1);
                }
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] finishEventForCharacter: Failed to set variable:', error);
                }
            },
            startEvent: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                if (!variableStore)
                    return;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const rawId = resolveScheduleEventIdFromStore(context, eventIdVar, userSelectedVar);
                const eventId = rawId ? String(rawId).trim() : '';
                if (!eventId)
                    return;
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const event = schedule.events.find((e) => e?.id === eventId);
                if (!event)
                    return;
                event.state = 'in-progress';
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] startEvent: Failed to set variable:', error);
                }
            },
            rescheduleEvent: (scheduleVar, eventId) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] rescheduleEvent: VariableStore not available');
                    return;
                }
                if (!scheduleVar || !eventId) {
                    console.error('[ScriptEvaluator] rescheduleEvent: Invalid parameters');
                    return;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const event = schedule.events.find((e) => e?.id === eventId);
                if (!event) {
                    console.error(`[ScriptEvaluator] rescheduleEvent: Event '${eventId}' not found`);
                    return;
                }
                event.state = 'proposed';
                event.proposedTimes = [];
                event.scheduledTimeEpochSeconds = null;
                delete event.realWorldLocation;
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] rescheduleEvent: Failed to set variable:', error);
                    throw error;
                }
            },
            rescheduleScheduleEvent: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                result.rescheduleEvent(scheduleVar, String(eventId));
            },
            /**
             * Remove a single event by id from the schedule variable.
             * @returns true when an event was removed
             */
            removeScheduleEvent: (scheduleVar, eventId) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] removeScheduleEvent: VariableStore not available');
                    return false;
                }
                if (!scheduleVar || !eventId || String(eventId).trim() === '') {
                    console.error('[ScriptEvaluator] removeScheduleEvent: Invalid parameters');
                    return false;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const trimmedId = String(eventId).trim();
                const eventIndex = schedule.events.findIndex((e) => e?.id === trimmedId);
                if (eventIndex === -1) {
                    return false;
                }
                schedule.events.splice(eventIndex, 1);
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] removeScheduleEvent: Failed to set variable:', error);
                    throw error;
                }
                return true;
            },
            /**
             * Remove all events whose title matches (trimmed, exact).
             * Clears schedule_event_id / user_selected_schedule_event_id when the selected id was removed.
             * @returns number of events removed
             */
            removeScheduleEventsByTitle: (scheduleVar, title, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] removeScheduleEventsByTitle: VariableStore not available');
                    return 0;
                }
                if (!scheduleVar || typeof scheduleVar !== 'string') {
                    console.error('[ScriptEvaluator] removeScheduleEventsByTitle: Invalid scheduleVar');
                    return 0;
                }
                const searchTitle = (title ?? '').toString().trim();
                if (!searchTitle) {
                    return 0;
                }
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const raw = variableStore.getVariable(scheduleVar, context);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const removedIds = new Set();
                const beforeCount = schedule.events.length;
                schedule.events = schedule.events.filter((e) => {
                    if (!e) {
                        return true;
                    }
                    if (String(e.title ?? '').trim() === searchTitle) {
                        if (e.id) {
                            removedIds.add(String(e.id));
                        }
                        return false;
                    }
                    return true;
                });
                const removedCount = beforeCount - schedule.events.length;
                if (removedCount === 0) {
                    return 0;
                }
                try {
                    variableStore.setVariable(scheduleVar, JSON.stringify(schedule), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] removeScheduleEventsByTitle: Failed to set variable:', error);
                    throw error;
                }
                const selectedId = resolveScheduleEventIdFromStore(context, eventIdVar, userSelectedVar);
                if (selectedId && removedIds.has(selectedId)) {
                    variableStore.setVariable(eventIdVar, '', context, 'string');
                    variableStore.setVariable(userSelectedVar, '', context, 'string');
                }
                return removedCount;
            },
            // Schedule board effect helpers - simplify adventure effect scripting
            syncScheduleEventSelection: (eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const id = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (id !== undefined && id !== null && String(id).trim() !== '') {
                    variableStore.setVariable(eventIdVar, id, ctx, 'string');
                }
            },
            createScheduleEventAndSelect: (scheduleVar, title, location, durationMinutes, requiredCharacters, startScene, requiresRealWorldLocation, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                const eventId = result.createEvent(scheduleVar, title, location, durationMinutes, requiredCharacters, startScene, requiresRealWorldLocation);
                if (eventId && variableStore) {
                    const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous', player.timeZoneOffsetMinutes);
                    variableStore.setVariable(eventIdVar, eventId, ctx, 'string');
                    variableStore.setVariable(userSelectedVar, eventId, ctx, 'string');
                }
            },
            /** Creates and selects a schedule event only when no event with the same title exists yet. */
            ensureScheduleEventAndSelect: (scheduleVar, title, location, durationMinutes, requiredCharacters, startScene, requiresRealWorldLocation, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
                if (conditionContext.hasScheduleEventWithTitle(scheduleVar, title)) {
                    return;
                }
                result.createScheduleEventAndSelect(scheduleVar, title, location, durationMinutes, requiredCharacters, startScene, requiresRealWorldLocation, eventIdVar, userSelectedVar);
            },
            commitScheduleTimeIfNotDeferred: (scheduleVar, epoch, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                variableStore.setVariable(proposedTimeVar, String(epoch), ctx, 'string');
                const raw = variableStore.getVariable(scheduleVar, ctx);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const event = schedule.events.find((e) => e?.id === String(eventId));
                if (event?.requiresRealWorldLocation) {
                    return;
                }
                try {
                    variableStore.setVariable('schedule_proposed_real_world_location', '', ctx, 'string');
                }
                catch {
                    // schedule_proposed_real_world_location may be undeclared in some adventures
                }
                result.proposeEventTime(scheduleVar, String(eventId), epoch);
            },
            finalizeScheduleTimeProposal: (scheduleVar, locationVar = 'schedule_real_world_location', eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                const proposedTime = variableStore.getVariable(proposedTimeVar, ctx);
                const location = (0, scheduleEventHelpers_1.normalizeRealWorldLocation)(variableStore.getVariable(locationVar, ctx));
                if (!eventId || proposedTime === undefined || proposedTime === null || !location)
                    return;
                const epoch = Number(proposedTime);
                if (!Number.isFinite(epoch))
                    return;
                result.proposeEventTime(scheduleVar, String(eventId), epoch, location);
            },
            proposeScheduleTimeNow: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                const now = Math.floor(ScriptEvaluator.resolveNowMs() / 1000);
                result.commitScheduleTimeIfNotDeferred(scheduleVar, now, eventIdVar, userSelectedVar, proposedTimeVar);
            },
            proposeScheduleTimeNext15Min: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const offsetMinutes = (0, timerDeadlineHelpers_1.resolvePlayerTimezoneOffsetMinutes)(player, variableStore);
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                const nowMs = ScriptEvaluator.resolveNowMs();
                const next = (0, dateTime_1.nextLocalSlotUtcMs)(offsetMinutes, nowMs, 5, 15);
                const epoch = Math.floor(next / 1000);
                result.commitScheduleTimeIfNotDeferred(scheduleVar, epoch, eventIdVar, userSelectedVar, proposedTimeVar);
            },
            proposeScheduleTimeNext30Min: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const offsetMinutes = (0, timerDeadlineHelpers_1.resolvePlayerTimezoneOffsetMinutes)(player, variableStore);
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                const nowMs = ScriptEvaluator.resolveNowMs();
                const next = (0, dateTime_1.nextLocalSlotUtcMs)(offsetMinutes, nowMs, 20, 15);
                const epoch = Math.floor(next / 1000);
                result.commitScheduleTimeIfNotDeferred(scheduleVar, epoch, eventIdVar, userSelectedVar, proposedTimeVar);
            },
            proposeScheduleTimeFromTodayHHMM: (scheduleVar, timeInputVar = 'schedule_time_input', eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const offsetMinutes = (0, timerDeadlineHelpers_1.resolvePlayerTimezoneOffsetMinutes)(player, variableStore);
                const v = variableStore.getVariable(timeInputVar, ctx);
                if (v === undefined || v === null || String(v).trim() === '')
                    return;
                const parts = String(v).split(':');
                const rawHour = Number(parts[0]);
                const rawMin = Number(parts[1]);
                if (!Number.isFinite(rawHour) || !Number.isFinite(rawMin))
                    return;
                const nowUtcMs = ScriptEvaluator.resolveNowMs();
                const todayStr = (0, dateTime_1.getLocalDateStringFromOffset)(offsetMinutes, nowUtcMs);
                const dateParts = todayStr.split('-').map(Number);
                const targetUtcMs = (0, dateTime_1.localDateTimeToUtcMs)(dateParts[0] ?? 0, dateParts[1] ?? 1, dateParts[2] ?? 1, rawHour, rawMin, offsetMinutes);
                if (targetUtcMs === null)
                    return;
                const minFutureUtcMs = nowUtcMs + 60 * 1000;
                const finalUtcMs = targetUtcMs < minFutureUtcMs ? minFutureUtcMs : targetUtcMs;
                const epoch = Math.floor(finalUtcMs / 1000);
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                result.commitScheduleTimeIfNotDeferred(scheduleVar, epoch, eventIdVar, userSelectedVar, proposedTimeVar);
            },
            proposeScheduleTimeFromDateAndTime: (scheduleVar, dateInputVar = 'schedule_date_input', timeInputVar = 'schedule_time_input', eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const offsetMinutes = (0, timerDeadlineHelpers_1.resolvePlayerTimezoneOffsetMinutes)(player, variableStore);
                const d = variableStore.getVariable(dateInputVar, ctx);
                const t = variableStore.getVariable(timeInputVar, ctx);
                if (d === undefined || d === null || t === undefined || t === null)
                    return;
                const dp = String(d).split('-');
                const tp = String(t).split(':');
                const year = Number(dp[0]);
                const month = Number(dp[1]);
                const day = Number(dp[2]);
                const hour = Number(tp[0]);
                const minute = Number(tp[1]);
                const targetUtcMs = (0, dateTime_1.localDateTimeToUtcMs)(year, month, day, hour, minute, offsetMinutes);
                if (targetUtcMs === null)
                    return;
                const minFutureUtcMs = ScriptEvaluator.resolveNowMs() + 60 * 1000;
                const finalUtcMs = targetUtcMs < minFutureUtcMs ? minFutureUtcMs : targetUtcMs;
                const epoch = Math.floor(finalUtcMs / 1000);
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                if (!eventId || String(eventId).trim() === '')
                    return;
                result.commitScheduleTimeIfNotDeferred(scheduleVar, epoch, eventIdVar, userSelectedVar, proposedTimeVar);
            },
            acceptScheduleEventTime: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time', proposedLocationVar = 'schedule_proposed_real_world_location') => {
                if (!variableStore)
                    return;
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                const eventId = resolveScheduleEventIdFromStore(ctx, eventIdVar, userSelectedVar);
                const proposedTime = variableStore.getVariable(proposedTimeVar, ctx);
                if (!eventId || proposedTime === undefined || proposedTime === null)
                    return;
                const epoch = Number(proposedTime);
                if (!Number.isFinite(epoch))
                    return;
                const raw = variableStore.getVariable(scheduleVar, ctx);
                let schedule = { events: [] };
                if (raw !== undefined && raw !== null && raw !== '') {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        schedule.events = Array.isArray(parsed?.events) ? parsed.events : [];
                    }
                    catch {
                        schedule.events = [];
                    }
                }
                const event = schedule.events.find((e) => e?.id === String(eventId));
                const proposedLocation = (0, scheduleEventHelpers_1.resolveRealWorldLocationForEvent)(event, variableStore.getVariable(proposedLocationVar, ctx));
                result.acceptEventTime(scheduleVar, String(eventId), epoch, proposedLocation);
            },
            addConversationMessage: (characterNameOrText, text) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] addConversationMessage: VariableStore not available');
                    return;
                }
                const MAX_ENTRIES = 100;
                const MAX_TEXT_LENGTH = 500;
                const characterName = text !== undefined ? characterNameOrText : player.characterName;
                const messageText = text !== undefined ? text : characterNameOrText;
                if (!messageText || typeof messageText !== 'string') {
                    console.error('[ScriptEvaluator] addConversationMessage: Invalid text parameter');
                    return;
                }
                // Strip HTML tags to prevent XSS (escape happens at output in generateCharacterComments)
                const sanitizedText = String(messageText).replace(/<[^>]*>/g, '');
                const trimmedText = sanitizedText.trim().slice(0, MAX_TEXT_LENGTH);
                const variableName = `${player.currentScene}_conversation`;
                const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                let entries;
                const raw = variableStore.getVariable(variableName, context);
                if (raw === undefined || raw === null || raw === '') {
                    entries = [];
                }
                else {
                    try {
                        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        entries = Array.isArray(parsed) ? parsed : [];
                    }
                    catch {
                        entries = [];
                    }
                }
                entries.push({
                    characterName: String(characterName || 'Unknown'),
                    text: trimmedText,
                    timestamp: Date.now(),
                    playerId: player.id
                });
                if (entries.length > MAX_ENTRIES) {
                    entries = entries.slice(-MAX_ENTRIES);
                }
                try {
                    variableStore.setVariable(variableName, JSON.stringify(entries), context, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] addConversationMessage: Failed to set variable:', error);
                    throw error;
                }
            },
            createEmailActionToken: (config) => {
                if (!emailActionTokenService) {
                    console.warn('[ScriptEvaluator] createEmailActionToken: EmailActionTokenService not available');
                    return '';
                }
                if (!config || typeof config !== 'object' || Array.isArray(config)) {
                    console.error('[ScriptEvaluator] createEmailActionToken: config must be an object');
                    return '';
                }
                if (!config.actionType || typeof config.actionType !== 'string' || config.actionType.trim() === '') {
                    console.error('[ScriptEvaluator] createEmailActionToken: actionType is required');
                    return '';
                }
                if (!config.actionPayload || typeof config.actionPayload !== 'object' || Array.isArray(config.actionPayload)) {
                    console.error('[ScriptEvaluator] createEmailActionToken: actionPayload must be an object');
                    return '';
                }
                if (!Number.isInteger(config.expiresInSeconds)) {
                    console.error('[ScriptEvaluator] createEmailActionToken: expiresInSeconds must be an integer');
                    return '';
                }
                if (config.expiresInSeconds < 60 || config.expiresInSeconds > 7 * 24 * 60 * 60) {
                    console.error('[ScriptEvaluator] createEmailActionToken: expiresInSeconds out of allowed range');
                    return '';
                }
                try {
                    return emailActionTokenService.createToken(config, gameSession.id, player.id);
                }
                catch (error) {
                    console.error('[ScriptEvaluator] createEmailActionToken: Failed to create token:', error);
                    return '';
                }
            },
            getEmailActionLink: (token) => {
                if (!emailActionTokenService) {
                    console.warn('[ScriptEvaluator] getEmailActionLink: EmailActionTokenService not available');
                    return '';
                }
                if (!token || typeof token !== 'string' || token.trim() === '') {
                    console.error('[ScriptEvaluator] getEmailActionLink: token is required');
                    return '';
                }
                try {
                    return emailActionTokenService.buildActionLink(token.trim());
                }
                catch (error) {
                    console.error('[ScriptEvaluator] getEmailActionLink: Failed to build link:', error);
                    return '';
                }
            },
            hasPendingEmailActionToken: () => {
                if (!emailActionTokenService)
                    return false;
                try {
                    return emailActionTokenService.hasPendingToken(gameSession.id, player.id);
                }
                catch {
                    return false;
                }
            },
            getPendingEmailActionSummary: () => {
                if (!emailActionTokenService)
                    return '';
                try {
                    return emailActionTokenService.getPendingTokenSummary(gameSession.id, player.id) || '';
                }
                catch {
                    return '';
                }
            },
            confirmPendingEmailActionToken: () => {
                if (!emailActionTokenService) {
                    return { success: false, code: 'SERVICE_UNAVAILABLE', message: 'EmailActionTokenService not available' };
                }
                try {
                    return emailActionTokenService.confirmPendingToken(gameSession.id, player.id);
                }
                catch (error) {
                    return { success: false, code: 'CONFIRM_FAILED', message: error instanceof Error ? error.message : 'Unknown error' };
                }
            },
            clearPendingEmailActionToken: () => {
                if (!emailActionTokenService)
                    return;
                try {
                    emailActionTokenService.clearPendingToken(gameSession.id, player.id);
                }
                catch (error) {
                    console.error('[ScriptEvaluator] clearPendingEmailActionToken: Failed to clear pending token:', error);
                }
            },
            // Email sending function - fire-and-forget
            sendEmail: (to, subject, content) => {
                if (!to || typeof to !== 'string' || to.trim().length === 0) {
                    console.error('[ScriptEvaluator] sendEmail: Invalid email address parameter');
                    return;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(to.trim())) {
                    console.error(`[ScriptEvaluator] sendEmail: Invalid email format: ${to}`);
                    return;
                }
                if (!subject || typeof subject !== 'string' || subject.trim().length === 0) {
                    console.error('[ScriptEvaluator] sendEmail: Invalid subject parameter');
                    return;
                }
                if (subject.length > 200) {
                    console.error(`[ScriptEvaluator] sendEmail: Subject too long (${subject.length} characters, max 200)`);
                    return;
                }
                if (!content || typeof content !== 'string' || content.trim().length === 0) {
                    console.error('[ScriptEvaluator] sendEmail: Invalid content parameter');
                    return;
                }
                if (content.length > 10000) {
                    console.error(`[ScriptEvaluator] sendEmail: Content too long (${content.length} characters, max 10000)`);
                    return;
                }
                if (!emailService) {
                    console.warn('[ScriptEvaluator] sendEmail: EmailService not available, email will not be sent');
                    return;
                }
                emailService.sendEmailAsync(to.trim(), subject.trim(), content.trim(), gameSession.id, player.id).catch((error) => {
                    console.error('[ScriptEvaluator] sendEmail: Email sending failed:', error);
                });
            },
            // Verification code email sender (fire-and-forget; updates user_* status variables)
            sendVerificationCodeEmail: (to, purpose, introText) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] sendVerificationCodeEmail: VariableStore not available');
                    return;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!to || typeof to !== 'string' || !emailRegex.test(to.trim())) {
                    try {
                        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                        variableStore.setVariable('user_email_verification_send_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_send_error', 'Invalid email address', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                const allowedPurposes = new Set(['mail_room_test', 'email_verification', 'password_reset']);
                if (!purpose || typeof purpose !== 'string' || !allowedPurposes.has(purpose.trim())) {
                    try {
                        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                        variableStore.setVariable('user_email_verification_send_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_send_error', 'Unsupported verification purpose', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                const intro = (introText ?? '').toString();
                if (intro.length > 2000) {
                    try {
                        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                        variableStore.setVariable('user_email_verification_send_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_send_error', 'Intro text is too long', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.setVariable('user_email_verification_send_status', 'inprogress', ctx, 'string');
                    variableStore.setVariable('user_email_verification_send_error', '', ctx, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] sendVerificationCodeEmail: Failed to set initial status:', error);
                    return;
                }
                if (!verificationCodeService) {
                    try {
                        variableStore.setVariable('user_email_verification_send_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_send_error', 'VerificationCodeService not available', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                verificationCodeService
                    .sendVerificationCodeEmailAsync(to.trim(), purpose.trim(), intro.trim(), gameSession.id, player.id)
                    .then((success) => {
                    try {
                        variableStore.setVariable('user_email_verification_send_status', success ? 'success' : 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_send_error', success ? '' : 'Unable to send verification code', ctx, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] sendVerificationCodeEmail: Failed to update status:', error);
                    }
                })
                    .catch((error) => {
                    console.error('[ScriptEvaluator] sendVerificationCodeEmail: Async send failed:', error);
                    try {
                        variableStore.setVariable('user_email_verification_send_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_send_error', error instanceof Error ? error.message : 'Unable to send verification code', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                });
            },
            // Verification code verifier (fire-and-forget; updates user_* verify status variables)
            verifyVerificationCode: (code, purpose) => {
                if (!variableStore) {
                    console.error('[ScriptEvaluator] verifyVerificationCode: VariableStore not available');
                    return;
                }
                const allowedPurposes = new Set(['mail_room_test', 'email_verification', 'password_reset']);
                const trimmedPurpose = (purpose ?? '').toString().trim();
                if (!trimmedPurpose || !allowedPurposes.has(trimmedPurpose)) {
                    try {
                        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                        variableStore.setVariable('user_email_verification_verify_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_verify_error', 'Unsupported verification purpose', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                const trimmedCode = (code ?? '').toString().trim();
                if (!/^\d{6}$/.test(trimmedCode)) {
                    try {
                        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                        variableStore.setVariable('user_email_verification_verify_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_verify_error', 'Verification code must be 6 digits', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
                try {
                    variableStore.setVariable('user_email_verification_verify_status', 'inprogress', ctx, 'string');
                    variableStore.setVariable('user_email_verification_verify_error', '', ctx, 'string');
                }
                catch (error) {
                    console.error('[ScriptEvaluator] verifyVerificationCode: Failed to set initial status:', error);
                    return;
                }
                if (!verificationCodeService) {
                    try {
                        variableStore.setVariable('user_email_verification_verify_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_verify_error', 'VerificationCodeService not available', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                    return;
                }
                verificationCodeService
                    .verifyVerificationCodeAsync(trimmedCode, trimmedPurpose, gameSession.id, player.id)
                    .then((result) => {
                    const success = Boolean(result?.success);
                    try {
                        variableStore.setVariable('user_email_verification_verify_status', success ? 'success' : 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_verify_error', success ? '' : (result?.message || 'Invalid or expired code'), ctx, 'string');
                    }
                    catch (error) {
                        console.error('[ScriptEvaluator] verifyVerificationCode: Failed to update status:', error);
                    }
                })
                    .catch((error) => {
                    console.error('[ScriptEvaluator] verifyVerificationCode: Async verify failed:', error);
                    try {
                        variableStore.setVariable('user_email_verification_verify_status', 'error', ctx, 'string');
                        variableStore.setVariable('user_email_verification_verify_error', error instanceof Error ? error.message : 'Verification failed', ctx, 'string');
                    }
                    catch {
                        // ignore
                    }
                });
            },
            ...(0, entityScriptHelpers_1.buildEntityScriptHelpers)(entitiesService, variableStore, player, gameSession, adventureManager),
            ...(0, foreignEntityScriptHelpers_1.buildForeignEntityScriptHelpers)(foreignEntityLookup, player)
        };
        return result;
    }
    /**
     * Execute script in controlled environment (inline + optional adventure prelude).
     */
    static execute(inline, context, options) {
        const contextKeys = Object.keys(context);
        const contextValues = Object.values(context);
        const fnSource = (0, adventureScriptValidation_1.buildExecutableFunctionSource)(options.prelude, inline, options.executionKind);
        const fn = new Function(...contextKeys, fnSource);
        return fn(...contextValues);
    }
    static isEmailService(service) {
        return !!service && typeof service.sendEmailAsync === 'function';
    }
}
exports.ScriptEvaluator = ScriptEvaluator;
//# sourceMappingURL=ScriptEvaluator.js.map